import { newestFirst } from './mail-index.js';
import {
  collection, collectionGroup, doc, query, where, orderBy, limit as fbLimit, startAfter,
  getDoc, getDocFromServer, getDocFromCache, getDocs, getDocsFromCache, getCountFromServer,
  setDoc, addDoc, updateDoc, deleteDoc, onSnapshot, runTransaction, increment, serverTimestamp,
} from './fb/firestore.js';
import { db, app, auth } from './firebase.js';
// THE PROJECTS A WEEK TOUCHES, denormalised onto the week (Karan 17/09). A
// project page needs the hours booked to ONE project, and Firestore cannot
// query a field inside an array of objects — so finding them meant reading
// every timesheet and looking inside its rows: 600 documents and 1.2 MB to use
// one row of one week. Each week now carries a flat array of the project ids
// and numbers its rows point at, which array-contains-any can index.
import { timesheetProjectKeys } from './timesheet-keys.js';
import { getFunctions, httpsCallable } from './fb/functions.js';
import { ORG_ID } from './config.js';
import { newTask, dayKey, STATES } from './tasks.js';

const col = (name) => collection(db, `organisations/${ORG_ID}/${name}`);
const orgPath = (rel) => `organisations/${ORG_ID}/${rel}`;
export { orgPath };

/**
 * OPEN THE CONNECTION BEFORE ANYBODY NEEDS IT.
 *
 * Every read on this page is local-first: the cache answers in zero
 * milliseconds and a sweep refreshes it behind you. That is why Track feels
 * instant right up until the first thing it CANNOT answer from cache — opening
 * a project record — and then that one read pays for the whole Firestore
 * connection at once. Measured on production: first record 7.3s, second record
 * 2.0s, and the five seconds between them is the WebChannel being negotiated
 * (and, on a bad network, re-negotiated after a 404 on its own session).
 *
 * The cache warm-up was moved to requestIdleCallback so it stopped competing
 * with the screen you were looking at — which was right, and which also pushed
 * the connection behind a user who clicks within eight seconds. So the
 * CONNECTION is warmed separately and immediately, with the cheapest read
 * Firestore offers: one aggregate count, one billed read, no documents.
 *
 * It resolves to nothing and swallows everything. Nobody waits on it.
 */
let connectionWarmed = null;
export function warmConnection() {
  if (connectionWarmed) return connectionWarmed;
  // A DOCUMENT read, not an aggregate. Measured: an aggregate count resolves in
  // five seconds and leaves the next document read taking thirty-seven, because
  // aggregates go over RunAggregationQuery and documents go over the Listen
  // channel — warming one warms neither the other's connection nor its session.
  // So this is the same shape of read a record makes: one small document, from
  // the server, on the transport that is about to matter.
  connectionWarmed = getDocFromServer(doc(db, 'app/version'))
    .then(() => true)
    .catch(() => false);
  return connectionWarmed;
}

// Fetch a single document by relative path (e.g. "clients/foo"), or null.
export async function getByPath(rel) {
  // An EMPTY path is not "the org", it is a bug in the caller. orgPath('') has
  // an even segment count, so Firestore resolves it as the organisation
  // DOCUMENT and hands back a record called "ASP" that looks like a client.
  // Refusing is how that surfaces as nothing rather than as the wrong company.
  const path = String(rel || '').trim();
  if (!path || path.split('/').length % 2 !== 0) return null;
  return cachedDoc(path);
}
export function getProjectById(id) { return getByPath(`projects/${id}`); }
export function getInstruction1(projectId) { return getByPath(`projects/${projectId}/instructions/1`); }

// Invoices linked to a project (by stored projectRef path). Bounded read.
export async function getProjectInvoices(projectId) {
  const projectRef = orgPath(`projects/${projectId}`);
  return cachedQuery(query(col('invoices'), where('projectRef', '==', projectRef), fbLimit(100)),
    { key: `invoices:${projectId}` });
}

// The six instruction/project work states that count as "live" (active work).
export const ACTIVE_STATUSES = ['In Progress', 'Review', 'Ready to Issue', 'Part Issued'];

/**
 * Cursor-paginated projects, highest project number first. Every read is bounded
 * by pageSize (Spark read-quota discipline). Returns { rows, lastDoc, done }.
 */
// Ordered by document id, NOT by number. Firestore silently omits any document
// that lacks the field you order by, so a project whose `number` was never set
// (or was nulled) vanished from Track entirely — the list, the search, Home, all
// of it — while still sitting in the database (Karan 28/08: one such project,
// Box Construction Ltd). Ordering on the id includes every document; the list
// screens sort by number themselves.
export async function pageProjects({ after = null, pageSize = 25 } = {}) {
  const clauses = [orderBy('__name__')];
  if (after) clauses.push(startAfter(after));
  clauses.push(fbLimit(pageSize));
  const snap = await getDocs(query(col('projects'), ...clauses));
  return {
    rows: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    lastDoc: snap.docs[snap.docs.length - 1] || null,
    done: snap.size < pageSize,
  };
}

/**
 * ALL projects, highest number first. Fetched in bounded chunks then cached
 * for the session (module-level), so the full-portfolio read costs its docs
 * once per session — repeat visits are free. Chunking keeps each individual
 * query bounded (Spark discipline) and safe if the collection grows.
 */
// Caches hold the IN-FLIGHT PROMISE, not just the resolved rows (perf audit
// 08/08): Home's Promise.all and the app.js warm-up used to race and fetch the
// same five whole collections twice on every cold boot. Sharing the promise
// dedupes concurrent callers; a failed fetch clears itself so retry works.
//
// LOCAL-FIRST (perf stage 2): Firestore persistence is on (firebase.js), so a
// repeat visit renders from the on-disk cache instantly; a background server
// sweep then replaces the session cache and fires `track:fresh` so the shell
// can gently re-render if anything actually changed. A fragmentary cache (from
// old paginated queries) is refused via a per-collection floor — below it we
// go straight to the server exactly as before.
const freshPing = (() => {
  let queued = false;
  return () => {
    if (queued) return; queued = true;
    setTimeout(() => { queued = false; document.dispatchEvent(new CustomEvent('track:fresh')); }, 400);
  };
})();
const changed = (a, b) => !a || !b || a.length !== b.length || (a[0] && b[0] && a[0].id !== b[0].id);

// An in-memory list lived as long as the tab (Karan 17/10: 35 prospect clients
// were in Firestore, but a tab open since the morning still drew the old 413).
// A hit older than two minutes is still served at once, but re-read in the
// background; if the server's list differs, `track:fresh` re-renders the screen.
const cacheAt = {};
const revalidating = {};
const MEMO_MAX_AGE = 120000;
function memoHit(key, server) {
  const hit = wholeCache[key];
  if (Date.now() - (cacheAt[key] || 0) > MEMO_MAX_AGE && !revalidating[key]) {
    revalidating[key] = true;
    Promise.resolve(hit).then(async (old) => {
      const fresh = await server();
      if (wholeCache[key] === hit) { wholeCache[key] = fresh; cacheAt[key] = Date.now(); }
      if (changed(fresh, old)) freshPing();
    }).catch(() => {}).finally(() => { revalidating[key] = false; });
  }
  return Promise.resolve(hit);
}

/**
 * LOCAL-FIRST SINGLE READS (perf stage 3, 24/09).
 *
 * `getDoc`/`getDocs` are SERVER-FIRST: while the tab is online they wait on the
 * network and only fall back to the cache when they cannot reach it. Persistence
 * being on does not change that. So opening a project re-fetched a document the
 * projects list had already put on disk seconds earlier — measured on a cold
 * connection, `getProjectById` alone took 32.6 seconds, and the ten reads behind
 * it added ten more. The list is fast for exactly one reason: it reads the cache
 * first (see the whole-collection helper above). These give a record the same
 * deal — answer from disk, sweep the server behind it, and fire `track:fresh`
 * only if the two actually differ.
 *
 * An empty cache is indistinguishable from an empty collection, so a genuinely
 * empty subcollection still pays a round trip. That is correct, just not faster.
 */
const sameDoc = (a, b) => JSON.stringify(a) === JSON.stringify(b);
// ORDER-INSENSITIVE (and it matters): the cache and the server hand back the
// same rows in different orders for a query with no orderBy, so a naive compare
// reports "changed" every single time — which fires `track:fresh`, re-renders,
// re-reads, and loops forever. Key by id and compare the set.
const sameRows = (a, b) => {
  if (a.length !== b.length) return false;
  // A row is normally {id, ...} but a custom `map` may return anything — a pair,
  // a string. Fall back to the row itself as its own key so those still compare.
  const idOf = (r) => (r && typeof r === 'object' && 'id' in r ? r.id : JSON.stringify(r));
  const key = (rows) => JSON.stringify(rows.map((r) => [idOf(r), r])
    .sort((x, y) => (String(x[0]) < String(y[0]) ? -1 : 1)));
  return key(a) === key(b);
};

export async function cachedDoc(rel) {
  const path = String(rel || '').trim();
  if (!path || path.split('/').length % 2 !== 0) return null;
  const ref = doc(db, orgPath(path));
  const shape = (snap) => (snap.exists() ? { id: snap.id, ...snap.data() } : null);
  try {
    const hit = await getDocFromCache(ref);
    if (hit.exists()) {
      const row = shape(hit);
      getDoc(ref).then((fresh) => { if (!sameDoc(shape(fresh), row)) freshPing(); }).catch(() => {});
      return row;
    }
  } catch (_) { /* not cached */ }
  return shape(await getDoc(ref));
}

/**
 * KNOWN-EMPTY MEMO. An empty cache and an empty collection are the same answer
 * from `getDocsFromCache`, so every query that legitimately returns nothing —
 * a project with no notes, no invoices, no pending suggestions — went to the
 * server every time and paid the full connection cost. Those were measured as
 * the last reads holding a record open: three of them at 43 seconds while the
 * seven cached reads had all landed inside one.
 *
 * So remember, per query, that the server said "none" last time, and answer
 * from that instantly while the sweep runs behind it. The risk is showing "no
 * invoices yet" for a moment on a project that has just had one raised — the
 * sweep corrects it and re-renders. That is the same bargain the projects list
 * already makes with stale rows, and it is made here for the same reason.
 */
const EMPTY_MEMO = 'track-known-empty';
// Entries EXPIRE. Without that, a project that has since gained an invoice shows
// "none" for as long as the sweep keeps failing — offline, a rules change, a
// quota wall — which is a wrong answer with no way out. An hour is far longer
// than a session and far shorter than a grievance.
const MEMO_TTL = 3600000;
const emptyMemo = () => {
  try {
    const m = JSON.parse(localStorage.getItem(EMPTY_MEMO) || '{}');
    const now = Date.now();
    return Object.fromEntries(Object.entries(m).filter(([, t]) => now - t < MEMO_TTL));
  } catch (_) { return {}; }
};
const rememberEmpty = (key, isEmpty) => {
  if (!key) return;
  try {
    const m = emptyMemo();
    if (isEmpty) m[key] = Date.now(); else delete m[key];
    localStorage.setItem(EMPTY_MEMO, JSON.stringify(m));
  } catch (_) { /* private window, or full — the memo is an optimisation only */ }
};
/** Forget every known-empty. Called on sign-out: the next person is not this one. */
export function clearEmptyMemo() {
  try { localStorage.removeItem(EMPTY_MEMO); } catch (_) { /* nothing to clear */ }
}

export async function cachedQuery(q, { key = null, map = (d) => ({ id: d.id, ...d.data() }) } = {}) {
  const sweep = (rows) => getDocs(q).then((fresh) => {
    const next = fresh.docs.map(map);
    rememberEmpty(key, next.length === 0);
    if (!sameRows(next, rows)) freshPing();
  }).catch(() => {});
  try {
    const hit = await getDocsFromCache(q);
    if (!hit.empty) {
      const rows = hit.docs.map(map);
      sweep(rows);
      return rows;
    }
    if (key && emptyMemo()[key]) { sweep([]); return []; }
  } catch (_) { /* not cached */ }
  const rows = (await getDocs(q)).docs.map(map);
  rememberEmpty(key, rows.length === 0);
  return rows;
}

let projectsCache = null;
async function fetchProjectsFromServer() {
  const rows = [];
  let after = null;
  // Big pages: fewer round trips is what makes first load fast (2 requests
  // today instead of 10). Hard stop at 4×2500 = 10k docs.
  for (let i = 0; i < 4; i++) {
    const { rows: chunk, lastDoc, done } = await pageProjects({ after, pageSize: 2500 });
    rows.push(...chunk);
    if (done || !lastDoc) break;
    after = lastDoc;
  }
  return byNumberDesc(rows);
}

// Newest first, as every caller has always been handed them. The QUERY no longer
// sorts by number — that silently excluded any document without one — so the
// sort happens here. A numberless project now sorts last instead of vanishing.
const byNumberDesc = (rows) =>
  rows.slice().sort((a, b) => String(b.number || '').localeCompare(String(a.number || '')));

// Re-read in the background once the list is two minutes old, as memoHit does for
// the other whole collections. Projects kept its own cache and never re-read, so a
// tab open before a Won enquiry became a project never listed it (A0346 → 8619,
// Karan 21/10/2026). The stale list is still served at once; `track:fresh`
// re-renders the screen if the server's list differs.
let projectsAt = 0;
let projectsRevalidating = false;
export function allProjects() {
  if (projectsCache) {
    if (Date.now() - projectsAt > MEMO_MAX_AGE && !projectsRevalidating) {
      projectsRevalidating = true;
      const old = projectsCache;
      fetchProjectsFromServer().then(async (fresh) => {
        projectsAt = Date.now();
        if (projectsCache !== old) return;               // invalidated meanwhile
        const prev = await old.catch(() => null);
        projectsCache = Promise.resolve(fresh);
        if (changed(fresh, prev)) freshPing();
      }).catch(() => {}).finally(() => { projectsRevalidating = false; });
    }
    return projectsCache;
  }
  projectsAt = Date.now();
  projectsCache = (async () => {
    try {
      const snap = await getDocsFromCache(query(col('projects'), orderBy('__name__'), fbLimit(10000)));
      if (snap.size >= 500) {                      // floor: a real mirror, not a fragment
        const rows = byNumberDesc(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        fetchProjectsFromServer().then((fresh) => {
          projectsCache = Promise.resolve(fresh);
          if (changed(fresh, rows)) freshPing();
        }).catch(() => {});
        return rows;
      }
    } catch (_) { /* nothing cached yet */ }
    return fetchProjectsFromServer();
  })();
  projectsCache.catch(() => { projectsCache = null; });
  return projectsCache;
}

// Cursor-paginated clients, alphabetical.
export async function pageClients({ after = null, pageSize = 25 } = {}) {
  const clauses = [orderBy('name')];
  if (after) clauses.push(startAfter(after));
  clauses.push(fbLimit(pageSize));
  const snap = await getDocs(query(col('clients'), ...clauses));
  return {
    rows: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    lastDoc: snap.docs[snap.docs.length - 1] || null,
    done: snap.size < pageSize,
  };
}

/**
 * Whole-collection session caches, same discipline as allProjects: fetched in
 * bounded chunks once per session, then free. A full session that visits every
 * screen costs roughly projects 2.9k + invoices 3.9k + clients 0.4k +
 * enquiries 0.2k reads — comfortably inside the Spark 50k/day for a small team.
 */
const wholeCache = {};
// Drop a whole-collection cache so the next read re-fetches (e.g. a Refresh button
// after new enquiries land). Pass no name to clear everything.
export function invalidateCache(name) {
  // projectsCache is a separate session cache (allProjects) — it was never cleared
  // here, so project edits served stale rows all session (audit 23/07). Clear it on
  // 'projects' or a full flush.
  if (!name || name === 'projects') projectsCache = null;
  if (name) delete wholeCache[name]; else Object.keys(wholeCache).forEach((k) => delete wholeCache[k]);
}

// Live listener on a whole collection (Karan 10/07 — "refresh like an email page").
// Fires cb(rows) on every change. Also keeps the session cache fresh so other
// screens see the update. Returns an unsubscribe fn — call it on navigation.
export function watchCollection(name, order, cb, constraints = []) {
  // `constraints` (extra where clauses) keeps a live board from re-downloading
  // an entire collection on subscribe (perf audit 08/08: the Office display
  // pulled all ~2.9k projects). A CONSTRAINED watch must NOT write the whole-
  // collection cache — it only holds a slice.
  const clauses = [...constraints, ...(order ? [orderBy(...order)] : [])];
  const q = clauses.length ? query(col(name), ...clauses) : col(name);
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (!constraints.length) { wholeCache[name] = rows; cacheAt[name] = Date.now(); }
    cb(rows);
  }, () => { /* listener error — ignore, manual refresh still works */ });
}
// Returns rows, caching the in-flight promise so concurrent callers share one
// fetch (see allProjects). watchCollection may later overwrite the entry with
// a plain rows array — both shapes await fine. `floor` gates the local-first
// path: only trust the on-disk cache when it plausibly holds the whole mirror.
function allOf(name, order, chunk = 2500, maxChunks = 4, floor = Infinity) {
  const server = async () => {
    const rows = [];
    let after = null;
    for (let i = 0; i < maxChunks; i++) {
      const clauses = [orderBy(...order)];
      if (after) clauses.push(startAfter(after));
      clauses.push(fbLimit(chunk));
      const snap = await getDocs(query(col(name), ...clauses));
      rows.push(...snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      if (snap.size < chunk) break;
      after = snap.docs[snap.docs.length - 1];
    }
    return rows;
  };
  if (wholeCache[name]) return memoHit(name, server);
  const p = (async () => {
    try {
      const snap = await getDocsFromCache(query(col(name), orderBy(...order), fbLimit(chunk * maxChunks)));
      if (snap.size >= floor) {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        server().then((fresh) => {
          if (wholeCache[name] === p) { wholeCache[name] = fresh; cacheAt[name] = Date.now(); }
          if (changed(fresh, rows)) freshPing();
        }).catch(() => {});
        return rows;
      }
    } catch (_) { /* nothing cached yet */ }
    return server();
  })();
  wholeCache[name] = p; cacheAt[name] = Date.now();
  p.catch(() => { if (wholeCache[name] === p) delete wholeCache[name]; });
  return p;
}

// Floors ≈ half the collection's known size — well above any fragment an old
// paginated query could have left behind, well below the real mirror.
export const allInvoices = () => allOf('invoices', ['number', 'desc'], 2500, 4, 1500);
export const allEnquiries = () => allOf('enquiries', ['receivedAt', 'desc'], 2500, 4, 80);
export const allClients = () => allOf('clients', ['name'], 2500, 4, 150);
export const allUsers = () => allOf('users', ['displayName'], 2500, 4, 4);

/**
 * Pre-computed aggregates, written by the sync each run (Metrics.gs):
 * 1-doc reads that replace whole-collection scans on the heavy screens.
 * Callers treat null / permission-denied as "fall back to computing locally".
 */
export const getDashboardMetrics = () => getByPath('metrics/dashboard');
export const getClientMetrics = () => getByPath('metrics/clients');
export const getFinanceMetrics = () => getByPath('finance/metrics'); // finance roles only

// Timestamp/ISO/Date → Date (Firestore SDK returns Timestamp objects).
export function asDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
}

// Users indexed by id (email local-part) — for resolving leads to real names.
/**
 * WHAT HOME ACTUALLY NEEDS, rather than every row ever written (Karan 23/09).
 *
 * Home awaited eight whole collections before it rendered anything — measured,
 * about 6,900 documents to summarise roughly 350, and ~2.3s warm against 11.3s
 * on a phone. These two are the bulk of it: 2,970 projects of which 2,312 are
 * Complete or Issued, and 3,934 invoices of which 3,679 are Paid.
 *
 * EXACTLY THE SAME ROWS Home was already filtering to, so no figure moves:
 *  · `status not-in ['Complete','Issued']` is the CLOSED filter home.js applies.
 *    A project with no status has it stored as an empty string, not missing, so
 *    it is still returned — checked against the live data, along with the fact
 *    that none of those 452 rows carries a due date, so none of them could ever
 *    have reached a figure anyway.
 *  · `paymentStatus == 'Invoiced'` is `outstandingInv`, character for character.
 *
 * Cache-first with a background sweep, the same shape as allOf: staleness here
 * costs freshness, never membership of a set the user can act on, and the sweep
 * fires `track:fresh` so the shell re-renders when the truth arrives.
 */
function narrow(key, q, sort) {
  // SORTED THE WAY THE WHOLE-COLLECTION READ SORTED. allProjects hands callers
  // byNumberDesc, and Home's "moving now" sort leaves rows with no due date in
  // whatever order they arrived — so dropping the sort quietly reshuffled the
  // tail of that list. Caught by diffing the rendered rows against production,
  // not by any test (Karan 23/09).
  const rows = (snap) => {
    const out = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return sort ? out.sort(sort) : out;
  };
  const server = async () => rows(await getDocs(q));
  if (wholeCache[key]) return memoHit(key, server);
  const p = (async () => {
    try {
      const snap = await getDocsFromCache(q);
      if (snap.size) {
        const cached = rows(snap);
        server().then((fresh) => {
          if (wholeCache[key] === p) { wholeCache[key] = fresh; cacheAt[key] = Date.now(); }
          if (changed(fresh, cached)) freshPing();
        }).catch(() => {});
        return cached;
      }
    } catch (_) { /* nothing cached yet */ }
    return server();
  })();
  wholeCache[key] = p; cacheAt[key] = Date.now();
  p.catch(() => { if (wholeCache[key] === p) delete wholeCache[key]; });
  return p;
}
const byNumberDescCmp = (a, b) => String(b.number || '').localeCompare(String(a.number || ''));

/**
 * Every status that is not Complete or Issued — listed, not negated.
 *
 * `not-in` is the obvious way to write this and it is a trap: measured against
 * live data it took over THIRTY SECONDS from the server, where reading all
 * 2,970 projects took 1.7s. Firestore cannot serve a negation from the index.
 * `in` is a disjunction of equality queries and returns in under a second.
 *
 * The cost of listing is that a status nobody thought of is invisible here. Two
 * things make that safe for the screens using it: a project with NO status
 * (452 of them, all legacy) carries no due date at all and so could never reach
 * a figure — checked against the live data — and any genuinely new status would
 * have to be added to the V1 vocabulary first, which is a deliberate act.
 */
export const OPEN_STATUSES = ['In Progress', 'Not Started', 'On Hold', 'Part Issued', 'Ready to Issue', 'Review', 'Void'];
export const openProjectsOnly = () =>
  narrow('projects:open', query(col('projects'), where('status', 'in', OPEN_STATUSES)), byNumberDescCmp);
export const outstandingInvoices = () =>
  narrow('invoices:outstanding', query(col('invoices'), where('paymentStatus', '==', 'Invoiced')), byNumberDescCmp);

/**
 * The two closed-status counts the pipeline shows. Deliberately SEPARATE and
 * never awaited by the render: getCountFromServer measured 4.8s for this pair,
 * which is slower than reading the documents was. The pipeline draws without
 * them and fills them in when they land.
 */
export async function closedProjectCounts() {
  const one = async (status) =>
    (await getCountFromServer(query(col('projects'), where('status', '==', status)))).data().count;
  const [issued, complete] = await Promise.all([one('Issued'), one('Complete')]);
  return { issued, complete };
}

export async function usersById() {
  const users = await allUsers();
  return Object.fromEntries(users.map((u) => [u.id, u]));
}

// Per-user break-even (cost) rates — Finance & above only (kept off the
// org-readable users doc). Returns { userId: £/hr }.
export async function financeRates() {
  const rows = await cachedQuery(col('financeRates'), {
    key: 'financeRates',
    map: (d) => [d.id, Number(d.data().breakEvenRate) || 0],
  });
  return Object.fromEntries(rows);
}
// Client alias memory — written whenever a human confirms/corrects an enquiry's
// client, read by the intake function BEFORE the LLM so the same sender (or a
// colleague on the same firm domain) is never mis-assigned twice.
const GENERIC_ALIAS_DOMAINS = /^(gmail|googlemail|hotmail|outlook|yahoo|ymail|icloud|me|live|msn|aol|btinternet|sky|virginmedia|talktalk|protonmail|proton|aspconsulting)\./;
const aliasKey = (v) => String(v || '').trim().toLowerCase().replace(/[^a-z0-9@._-]+/g, '_');
export async function rememberClientAlias(email, clientRef, clientName, by) {
  const em = String(email || '').trim().toLowerCase();
  if (!em || !clientRef) return;
  const rec = { clientRef, clientName: clientName || null, by: by || null, at: new Date().toISOString() };
  await setDoc(doc(db, orgPath(`clientAliases/${aliasKey(em)}`)), { ...rec, email: em }, { merge: true });
  const dom = (em.match(/@([^\s>]+)/) || [])[1];
  if (dom && !GENERIC_ALIAS_DOMAINS.test(dom + '.')) {
    await setDoc(doc(db, orgPath(`clientAliases/dom_${aliasKey(dom)}`)), { ...rec, domain: dom }, { merge: true });
  }
}

export function setFinanceRate(userId, breakEvenRate, by) {
  return setDoc(doc(db, orgPath(`financeRates/${userId}`)), {
    breakEvenRate: Number(breakEvenRate) || 0, updatedBy: by || null, updatedAt: new Date().toISOString(),
  }, { merge: true });
}
// Annual salary lives in the Finance-only financeRates doc (the users doc is
// org-readable, so salary must NOT go there) — Karan 27/07. Feeds the monthly
// expenses statement. Returns { userId: salary }.
export async function financeSalaries() {
  const snap = await getDocs(col('financeRates'));
  return Object.fromEntries(snap.docs.map((d) => [d.id, Number(d.data().salary) || 0]));
}
export function setUserSalary(userId, salary, by) {
  return setDoc(doc(db, orgPath(`financeRates/${userId}`)), {
    salary: Number(salary) || 0, updatedBy: by || null, updatedAt: new Date().toISOString(),
  }, { merge: true });
}

// ── The salary plan (Finance FY 2026–27 stage 4, Accounts › Budget): the whole
// financeRates doc per person — salary beside bonus, employerNiPct, pensionPct,
// startMonth, endMonth. Behind the step-up like everything else in financeRates.
// Returns { userId: data }.
export async function financePlanRates() {
  const snap = await getDocs(col('financeRates'));
  return Object.fromEntries(snap.docs.map((d) => [d.id, d.data()]));
}
export function setFinancePlanFields(userId, fields, by) {
  return setDoc(doc(db, orgPath(`financeRates/${userId}`)), {
    ...fields, updatedBy: by || null, updatedAt: new Date().toISOString(),
  }, { merge: true });
}
// The budget for one FY (budgets/{fy}, fy '2026-27'): Owner/Director write,
// Finance read (rules). budgets/history is the one-off seed of past sales.
export async function getBudget(fy) {
  const snap = await getDoc(doc(db, orgPath(`budgets/${fy}`)));
  return snap.exists() ? snap.data() : null;
}
export function saveBudget(fy, data) {
  return setDoc(doc(db, orgPath(`budgets/${fy}`)), data);
}
export async function getBudgetHistory() {
  const snap = await getDoc(doc(db, orgPath('budgets/history')));
  return snap.exists() ? snap.data() : null;
}

// ── Employment record — NI number, date of birth, start date, pension opt-in
// (Karan 26/08). Personal data, so it gets its OWN Finance-gated collection for
// the same reason salary does: the users doc is org-readable. Returns
// { userId: record }.
export async function hrRecords() {
  const snap = await getDocs(col('hrRecords'));
  return Object.fromEntries(snap.docs.map((d) => [d.id, d.data()]));
}
export function setHrRecord(userId, fields, by) {
  return setDoc(doc(db, orgPath(`hrRecords/${userId}`)), {
    ...fields, updatedBy: by || null, updatedAt: new Date().toISOString(),
  }, { merge: true });
}

// ── History for the private money fields — salary, break-even cost, charge-out
// (Karan 26/08).
//
// THE POINT: addRecordChange writes to `changes`, which is `allow read: if
// inOrg(orgId)` — org-readable. Every salary edit was therefore recording the
// old and new figure where the whole company could read it. Anything flagged
// `money` now comes HERE instead: under the Finance-gated financeRates doc, and
// append-only in the rules, because an audit trail that can be edited is not an
// audit trail.
export function addFinanceLogEntry(userId, entry) {
  return addDoc(collection(db, orgPath(`financeRates/${userId}/financeLog`)), entry);
}
export async function listFinanceLog(userId, max = 60) {
  const snap = await getDocs(query(collection(db, orgPath(`financeRates/${userId}/financeLog`)), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.at || '').localeCompare(String(a.at || ''))); // newest first
}

// --- V2-NATIVE WRITE PATHS (first write cutover, Karan sign-off 06/07/2026).
// Rules are the boundary; these helpers just address the right paths. ---

// ── Timesheet concurrency (Karan 27/08) ────────────────────────────────────
// A timesheet is one doc holding a `rows` ARRAY, and Firestore's merge does not
// merge arrays — it replaces them. So two windows on the same week each wrote
// their own stale copy of `rows` and whichever saved last silently dropped the
// other's hours. Every writer now bumps `rev`, and a guarded save states the
// rev it was working from: if the doc has moved on the write is REFUSED, so
// nothing is lost and the screen can say so. `rev` missing == 0 (every existing
// doc), so the first guarded save on an old sheet sets it to 1.
export class StaleTimesheet extends Error {
  constructor(serverRev) {
    super('This week changed in another window.');
    this.name = 'StaleTimesheet';
    this.serverRev = serverRev;
  }
}
const revOf = (snap) => (snap.exists() ? Number(snap.data().rev || 0) : 0);

/**
 * Save a whole timesheet. Pass `expectedRev` to guard the write — without it
 * this is the old unguarded behaviour, kept for callers that own the doc
 * outright (the seeding write for a week that does not exist yet).
 * Resolves to the new rev.
 */
export { timesheetProjectKeys };

export function saveTimesheet(id, data, expectedRev) {
  invalidateTimesheets();
  const ref = doc(db, orgPath(`timesheets/${id}`));
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur = revOf(snap);
    if (expectedRev !== undefined && cur !== Number(expectedRev || 0)) throw new StaleTimesheet(cur);
    // Only when this write carries rows: a partial merge must not blank the
    // index that the rows already on the document earned.
    const keys = data && Array.isArray(data.rows) ? { projectKeys: timesheetProjectKeys(data.rows) } : {};
    tx.set(ref, { ...data, ...keys, rev: cur + 1 }, { merge: true });
    return cur + 1;
  });
}

/** The open week, live. Returns an unsubscribe. */
export function watchTimesheet(id, cb) {
  return onSnapshot(doc(db, orgPath(`timesheets/${id}`)),
    (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    () => { /* listener error — the manual reload still works */ });
}
export async function getTimesheet(id) {
  const snap = await getDoc(doc(db, orgPath(`timesheets/${id}`)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
// All timesheets for a week (reviewers) or a user (own history). Unbounded
// filters are fine: the collection grows ~team-size docs per week.
// Short-TTL caches for the timesheet reads that Home / Team / project pages
// re-issued on every render (perf audit 08/08). Any timesheet write clears
// them, so your own edits are never served stale.
const ttlCache = {};
function ttl(key, ms, fetcher) {
  const hit = ttlCache[key];
  if (hit && Date.now() - hit.at < ms) return hit.p;
  const p = fetcher();
  ttlCache[key] = { at: Date.now(), p };
  p.catch(() => { if (ttlCache[key] && ttlCache[key].p === p) delete ttlCache[key]; });
  return p;
}
export function invalidateTimesheets() {
  Object.keys(ttlCache).forEach((k) => { if (k.startsWith('ts:')) delete ttlCache[k]; });
}
export function timesheetsForWeek(weekStart) {
  return ttl(`ts:week:${weekStart}`, 120000, async () => {
    const snap = await getDocs(query(col('timesheets'), where('weekStart', '==', weekStart)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  });
}
// Submitted-but-unreviewed sheets across ALL weeks (for the Approvals nav dot —
// Karan 20/07: last week's submitted sheets weren't lighting it).
export async function submittedTimesheets(max = 200) {
  const snap = await getDocs(query(col('timesheets'), where('status', '==', 'submitted'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
// Mark (or unmark) a single timesheet row as training (reviewers) — Karan 14/07.
export async function setTimesheetRowTraining(sheetId, rowIndex, training) {
  const ref = doc(db, orgPath(`timesheets/${sheetId}`));
  invalidateTimesheets();
  // Read-modify-write of the whole rows array, so it has to be a transaction —
  // outside one it would drop hours logged between the read and the write.
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const rows = (snap.data().rows || []).slice();
    if (!rows[rowIndex]) return;
    rows[rowIndex] = { ...rows[rowIndex], training: !!training };
    tx.update(ref, { rows, projectKeys: timesheetProjectKeys(rows), rev: revOf(snap) + 1 });
  });
}
/**
 * The weeks that booked time to ONE project — one indexed read, not the lot.
 *
 * DEPLOY-ORDER SAFE ON PURPOSE. The index only exists on weeks written since
 * `scripts/backfill-timesheet-index.py` ran, so an indexed query on a database
 * that has not been backfilled would quietly answer "no time logged" for every
 * project — and a wrong number nobody can see is worse than a slow one
 * everybody can. The backfill leaves a marker; until it is there this falls
 * back to the old whole read and the old answer, so nothing has to ship in a
 * particular order.
 */
let indexReadyP = null;
function timesheetIndexReady() {
  if (!indexReadyP) {
    indexReadyP = getDoc(doc(db, orgPath('meta/timesheetIndex')))
      .then((snap) => snap.exists() && !!snap.data().backfilledAt)
      .catch(() => false);          // unreadable marker = assume not indexed
  }
  return indexReadyP;
}
const byWeekDesc = (a, b) => String(b.weekStart || '').localeCompare(String(a.weekStart || ''));
/** The keys a row could be matched by, exactly as project detail matches them. */
const keysFor = (projectId, projectNo) => [...new Set(
  [projectId, projectNo == null ? '' : String(projectNo).trim()].filter(Boolean).map(String),
)];

// The same ceiling the old whole-collection read used, so this can only ever be
// more complete than what it replaces. A real project spans a few dozen weeks;
// the busiest key in the data is ASP's own internal time at 382, still inside it.
export async function timesheetsForProject(projectId, projectNo, max = 600) {
  const keys = keysFor(projectId, projectNo);
  if (!keys.length) return [];
  if (!(await timesheetIndexReady())) {
    const all = await allTimesheets();
    return all.filter((sh) => (sh.rows || []).some(
      (r) => (r.projectId && keys.includes(String(r.projectId)))
        || (r.number != null && keys.includes(String(r.number).trim())),
    ));
  }
  // FROM THE SERVER, NOT THE CACHE — and this one is worth the round trip.
  //
  // `getDocsFromCache` answers from whatever copies this browser happens to
  // hold, and membership of THIS query depends on a field written server-side
  // by the backfill. A browser whose cached copies predate that write matches
  // only the few it has refreshed since, returns them, and looks entirely
  // successful: a project quietly reports three of its five weeks and a number
  // that is simply wrong. Caught doing exactly that on live data (17/09).
  //
  // The whole-collection reads can be cache-first because staleness there costs
  // freshness, not membership — the set is the same either way. Here it IS the
  // membership. The TTL below still means one round trip per project per five
  // minutes, and offline the SDK serves getDocs from the cache anyway; this
  // only stops it PREFERRING a partial answer.
  //
  // No orderBy: array-contains-any plus a sort on another field would need a
  // composite index, and a handful of rows sort fine here.
  return ttl(`ts:proj:${keys.join('|')}`, 300000, async () => {
    const snap = await getDocs(query(col('timesheets'),
      where('projectKeys', 'array-contains-any', keys), fbLimit(max)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(byWeekDesc);
  });
}

export function allTimesheets(max = 600) {
  // Newest weeks first: after importing historical weeks, this keeps the live
  // (recent) sheets inside the cap so project time-tracked still counts them.
  //
  // Deliberately still a SERVER read. It was briefly made cache-first to rescue
  // the project page, which used to await all 600 sheets; that page now asks
  // for the weeks it actually needs (timesheetsForProject above), so the reason
  // is gone — and the callers left, Team and Ask Track, show people's logged
  // hours, where a stale answer is worse than a slow one.
  return ttl(`ts:all:${max}`, 300000, async () => {
    const snap = await getDocs(query(col('timesheets'), orderBy('weekStart', 'desc'), fbLimit(max)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  });
}

// Reviewer-side field writes (status, review notes, edited rows). These bump
// `rev` too, so the owner's window sees the change and their next save is
// refused rather than overwriting what the reviewer just did.
export function updateTimesheet(id, fields, { expectStatus } = {}) {
  // ROWS MAY NOT COME THROUGH HERE. A caller passing a rows array has read it
  // at some earlier point, edited a cell in it, and would write the whole array
  // back — erasing every hour booked in between, with no guard and no error.
  // The reviewer screen did exactly that. editTimesheetRows() is the way.
  if (fields && Object.prototype.hasOwnProperty.call(fields, 'rows')) {
    throw new Error('updateTimesheet cannot write rows — use editTimesheetRows, which edits inside the transaction.');
  }
  invalidateTimesheets();
  const ref = doc(db, orgPath(`timesheets/${id}`));
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('That timesheet no longer exists.');
    // Approve submitted (Time › Approvals, 25/10/2026) approves what the screen
    // LOADED; a sheet pulled back to draft since is left alone, not approved.
    if (expectStatus && (snap.data().status || 'draft') !== expectStatus) throw new Error('It changed since this screen loaded — it is no longer submitted.');
    tx.update(ref, { ...fields, rev: revOf(snap) + 1 });
  });
}

/**
 * Edit a week's rows INSIDE the transaction.
 *
 * `transform` is handed the rows as they are AT THIS MOMENT and returns the
 * rows as they should be. That is the whole point: a reviewer's correction is
 * applied to the current sheet rather than to the copy their screen loaded
 * minutes ago, so hours logged in between survive the edit instead of being
 * overwritten by a stale array.
 */
export function editTimesheetRows(id, transform, fields = {}) {
  invalidateTimesheets();
  const ref = doc(db, orgPath(`timesheets/${id}`));
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('That timesheet no longer exists.');
    const current = (snap.data().rows || []).map((r) => ({ ...r, days: { ...(r.days || {}) } }));
    const rows = transform(current);
    if (!Array.isArray(rows)) throw new Error('The row edit returned nothing — the sheet was not changed.');
    // Every path that writes rows rewrites the index with them, so the two can
    // never drift — cheaper than reasoning about which edits can move a project.
    tx.update(ref, { ...fields, rows, projectKeys: timesheetProjectKeys(rows), rev: revOf(snap) + 1 });
    return rows;
  });
}

export function logTelemetry(data) { return addDoc(col('telemetry'), data); }

// Reported issues (Owner/Director admin view). Single equality filter → no
// composite index needed; we sort newest-first client-side.
export async function listIssues(max = 300) {
  const snap = await getDocs(query(col('telemetry'), where('kind', '==', 'issue'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export function updateIssue(id, fields) {
  return updateDoc(doc(db, orgPath(`telemetry/${id}`)), fields);
}

// App version (Karan 13/07): the deploy writes the shipped BUILD here; the app
// watches it and prompts everyone to reload when a newer version lands.
export function watchAppVersion(cb) {
  return onSnapshot(doc(db, 'app/version'),
    (snap) => cb(snap.exists() ? (snap.data() || {}) : {}),
    () => { /* ignore listen errors */ });
}

/**
 * Mail in real time (07/10/2026): the person's own pulse, written by the Gmail
 * push handler (functions/mail-pulse.js) whenever their inbox or sent mail
 * changes. Returns the unsubscribe. A listen error is silent — Mail's safety
 * net still refreshes.
 */
export function watchMailPulse(cb) {
  const uid = auth && auth.currentUser && auth.currentUser.uid;
  if (!uid) return () => {};
  return onSnapshot(doc(db, orgPath(`mailPulse/${uid}`)),
    (snap) => { if (snap.exists()) cb(snap.data() || {}); },
    () => { /* no pulse — the safety net covers it */ });
}

// ── Responsiveness (Karan 08/10/2026, "Track mail upgrades" turn 4) ─────────
// organisations/asp/responseQueries/{id} and responseStats/{firm|person_<email>},
// written only by the server (functions/responsiveness.js). The Owner and
// Directors read everyone; others only their own (the rules enforce it, so the
// query must say owner == me for them).
export async function listResponseQueries({ email, everyone = false, sinceDays = 91 } = {}) {
  const since = new Date(Date.now() - sinceDays * 86400000).toISOString();
  const q = everyone
    ? query(col('responseQueries'), where('arrivedAt', '>=', since), fbLimit(2000))
    : query(col('responseQueries'), where('owner', '==', String(email || '').toLowerCase()), fbLimit(1000));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export function watchResponseQueries({ email, everyone = false }, cb) {
  const q = everyone
    ? query(col('responseQueries'), where('state', 'in', ['open', 'holding']), fbLimit(500))
    : query(col('responseQueries'), where('owner', '==', String(email || '').toLowerCase()), fbLimit(500));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => cb(null));
}
export async function getResponseStats(key) {
  const snap = await getDoc(doc(db, orgPath(`responseStats/${key}`))).catch(() => null);
  return snap && snap.exists() ? snap.data() : null;
}
/** The query a Gmail thread belongs to, if the reader may see it (own, or Owner/Director). */
export async function responseQueryForThread(threadId, { email, everyone = false } = {}) {
  if (!threadId) return null;
  const clauses = [where('gmailThreads', 'array-contains', String(threadId))];
  if (!everyone) clauses.push(where('owner', '==', String(email || '').toLowerCase()));
  const snap = await getDocs(query(col('responseQueries'), ...clauses, fbLimit(1))).catch(() => null);
  return snap && !snap.empty ? { id: snap.docs[0].id, ...snap.docs[0].data() } : null;
}
export const correctResponseQuery = (data) => callFunction('responsivenessCorrect', data);

// ── Mail approval (Karan 07/10/2026) ────────────────────────────────────────
// organisations/asp/mailApprovals/{id}: an author asks another person to read,
// suggest changes to and approve an email. Emails are stored lower-cased; the
// rules compare them to the signed-in person's. Small files from the author's
// computer ride in files/{n} as base64.
export async function createMailApproval(data, files = []) {
  const uid = auth && auth.currentUser && auth.currentUser.uid;
  const ref = await addDoc(col('mailApprovals'), { ...data, authorUid: uid, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
  await Promise.all(files.map((f, i) => setDoc(doc(db, orgPath(`mailApprovals/${ref.id}/files/${i}`)), f)));
  return ref.id;
}
export function updateMailApproval(id, fields) {
  return updateDoc(doc(db, orgPath(`mailApprovals/${id}`)), { ...fields, updatedAt: new Date().toISOString() });
}
export async function getMailApprovalFile(id, path) {
  const snap = await getDoc(doc(db, orgPath(`mailApprovals/${id}/${path}`)));
  return snap.exists() ? snap.data() : null;
}
const approvalQueries = (email) => {
  const e = String(email || '').toLowerCase();
  return [query(col('mailApprovals'), where('authorEmail', '==', e), fbLimit(100)),
    query(col('mailApprovals'), where('reviewerEmail', '==', e), fbLimit(100))];
};
/** Both sides of every approval this person is in, live. Returns unsubscribe. */
export function watchMailApprovals(email, cb) {
  const parts = [new Map(), new Map()];
  const emit = () => cb([...new Map([...parts[0], ...parts[1]]).values()]);
  const offs = approvalQueries(email).map((q, i) => onSnapshot(q,
    (snap) => { parts[i] = new Map(snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }])); emit(); },
    () => { /* not readable yet — the list stays as it was */ }));
  return () => offs.forEach((off) => off());
}
export async function listMailApprovals(email) {
  const snaps = await Promise.all(approvalQueries(email).map((q) => getDocs(q).catch(() => ({ docs: [] }))));
  const by = new Map();
  snaps.forEach((s) => s.docs.forEach((d) => by.set(d.id, { id: d.id, ...d.data() })));
  return [...by.values()];
}

export function addExpense(data) { return addDoc(col('expenses'), data); }
/** The indicative INR→GBP rate (fxRates/INR_GBP, written by the server daily), or null. */
export async function getFxRate() {
  const snap = await getDoc(doc(db, orgPath('fxRates/INR_GBP')));
  return snap.exists() ? snap.data() : null;
}
export function updateExpense(id, fields) {
  return updateDoc(doc(db, orgPath(`expenses/${id}`)), fields);
}
export function deleteExpense(id) { return deleteDoc(doc(db, orgPath(`expenses/${id}`))); }
export async function listExpenses({ userEmail = null, ownerUid = null, max = 800 } = {}) {
  // A phone-only member has no email: their own expenses carry ownerUid (me.js).
  const clauses = userEmail ? [where('userEmail', '==', userEmail)] : ownerUid ? [where('ownerUid', '==', ownerUid)] : [];
  const snap = await getDocs(query(col('expenses'), ...clauses, fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Manual mail → job/category/archived assignments made from the inbox. Keyed on
// the email's rfc822 Message-Id (stable across mailboxes) so an assignment one
// person makes is SHARED — it allocates that mail to the project for everyone,
// like a TRACK// tag (Karan 06/08). Separate from the archiver-owned, track@-built
// projectEmails index; the correspondence tab (item 7) will union the two.
export const mailAssignmentId = (messageId) =>
  String(messageId || '').replace(/[<>]/g, '').replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 200) || 'unknown';
export function setMailAssignment(messageId, fields) {
  const id = mailAssignmentId(messageId);
  return setDoc(doc(db, orgPath(`mailAssignments/${id}`)), { messageId, ...fields }, { merge: true });
}
export async function listMailAssignments({ max = 500 } = {}) {
  const snap = await getDocs(query(col('mailAssignments'), fbLimit(max)));
  const out = {};
  snap.docs.forEach((d) => { const x = d.data(); if (x.messageId) out[x.messageId] = { id: d.id, ...x }; });
  return out; // keyed by messageId → O(1) merge against inbox threads
}

// Record change list (edit-patterns 6h): every committed edit appends ONE
// immutable line — {record:'users/<id>'|'projects/<no>'|…, field, label, from, to,
// by, byName, at(iso), reason?, money?}. Append-only by design: nothing is ever
// deleted and there is NO undo (putting a value back is just another edit that
// reads as one on the list). "Nothing changes silently" — every edit writes here.
export function addRecordChange(data) { return addDoc(col('changes'), data); }
export async function listRecordChanges(record, max = 60) {
  const snap = await getDocs(query(col('changes'), where('record', '==', String(record)), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.at || '').localeCompare(String(a.at || ''))); // newest first
}

// ── Time off (Calendar and leave, v4.5.0, Karan 25/10/2026) ─────────────────
// A request is made, decided and moved through the time-off callables
// (functions/leave.js) — the rules no longer let a browser create one or change
// its status. The browser may still record the shared-calendar event id
// (approver) or edit its own note while it waits.
export function updateLeaveRequest(id, fields) {
  return updateDoc(doc(db, orgPath(`leaveRequests/${id}`)), fields);
}
const LEAVE_APPROVERS = ['Owner', 'Director', 'Finance', 'Coordinator'];
async function myClaims() {
  const user = auth && auth.currentUser;
  if (!user) return { roles: [], email: '' };
  try {
    const r = await user.getIdTokenResult();
    return { roles: Array.isArray(r.claims.roles) ? r.claims.roles : [], email: String(user.email || '').toLowerCase() };
  } catch (_) { return { roles: [], email: String(user.email || '').toLowerCase() }; }
}
let boardMemo = null;   // { at, key, p } — the who's-off board, two minutes
/** Who is where (dates, first names, off or home); the reason only for you and approvers. */
export function leaveBoard({ from, to } = {}) {
  const key = `${from || ''}|${to || ''}`;
  if (boardMemo && boardMemo.key === key && Date.now() - boardMemo.at < 120000) return boardMemo.p;
  const p = callFunction('leaveBoard', { from, to }).catch((e) => { if (boardMemo && boardMemo.p === p) boardMemo = null; throw e; });
  boardMemo = { at: Date.now(), key, p };
  return p;
}
const forgetBoard = () => { boardMemo = null; };
export async function requestTimeOff(data) { const r = await callFunction('requestTimeOff', data); forgetBoard(); return r; }
export async function decideTimeOff(data) { const r = await callFunction('decideTimeOff', data); forgetBoard(); return r; }
export async function moveTimeOff(data) { const r = await callFunction('moveTimeOff', data); forgetBoard(); return r; }
export async function setTimeOffRules(rules) { const r = await callFunction('setTimeOffRules', { rules }); forgetBoard(); return r; }
/** Withdraw your own request while it still waits (the rules allow exactly this). */
export async function withdrawLeaveRequest(id) { await deleteDoc(doc(db, orgPath(`leaveRequests/${id}`))); forgetBoard(); }

/**
 * Leave as the older screens read it (Timesheet, Tasks, Team, Office, Meeting,
 * the phone). SICKNESS IS NEVER SHOWN TO OTHERS (firestore.rules): an approver
 * reads every request; anyone else reads their own, everyone's other kinds,
 * and colleagues' sick days only as "off" from the board — no kind, no note.
 * Working from home is not leave, so it is left out unless asked for.
 */
export async function listLeaveRequests(max = 300, { wfh = false } = {}) {
  const { roles, email } = await myClaims();
  let rows;
  if (roles.some((r) => LEAVE_APPROVERS.includes(r)) || !email) {
    const snap = await getDocs(query(col('leaveRequests'), fbLimit(max)));
    rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } else {
    const [own, others] = await Promise.all([
      getDocs(query(col('leaveRequests'), where('userEmail', '==', email), fbLimit(max))),
      getDocs(query(col('leaveRequests'), where('kind', 'in', ['annual', 'other', 'wfh']), fbLimit(max))),
    ]);
    const byId = new Map();
    for (const d of [...own.docs, ...others.docs]) byId.set(d.id, { id: d.id, ...d.data() });
    rows = [...byId.values()];
    // Colleagues' sick days, as "off" only. Best-effort: never holds a screen up.
    try {
      const today = new Date();
      const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const from = iso(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 60));
      const to = iso(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 300));
      const b = await Promise.race([leaveBoard({ from, to }), new Promise((_, no) => setTimeout(() => no(new Error('slow')), 4000))]);
      const emailOf = new Map((b.people || []).map((p) => [p.id, p.email]));
      const nameOf = new Map((b.people || []).map((p) => [p.id, p.first]));
      for (const e of b.entries || []) {
        if (byId.has(e.id) || e.mine || e.type !== 'off' || !emailOf.get(e.uid)) continue;
        rows.push({ id: e.id, userEmail: emailOf.get(e.uid), userName: nameOf.get(e.uid) || '', kind: 'off', from: e.from, to: e.to,
          halfDay: e.from === e.to && (e.fromHalf === 'pm' || e.toHalf === 'am'), fromHalf: e.fromHalf, toHalf: e.toHalf,
          status: e.status === 'approved' ? 'approved' : 'requested' });
      }
    } catch (_) { /* without the board, colleagues' sick days are simply not listed */ }
  }
  return wfh ? rows : rows.filter((r) => r.kind !== 'wfh');
}

export async function listInstructions(projectId) {
  const rows = await cachedQuery(collection(db, orgPath(`projects/${projectId}/instructions`)),
    { key: `instructions:${projectId}` });
  return rows.sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0));
}
// Auto-resume (Karan 11/08): activity takes a project OFF hold by itself —
// logging time, raising an invoice, adding an instruction. Restores the status
// it was holding at (statusBeforeHold, else In Progress), keeps heldReason in
// the change list, and records HOW it resumed. Fire-and-forget at call sites.
export async function resumeIfHeld(projectId, by, byName, how) {
  try {
    const ref = doc(db, orgPath(`projects/${projectId}`));
    const snap = await getDoc(ref);
    if (!snap.exists() || snap.data().status !== 'On Hold') return false;
    const p = snap.data();
    const to = p.statusBeforeHold || 'In Progress';
    const at = new Date().toISOString();
    await updateDoc(ref, { status: to, heldReason: null, statusBeforeHold: null,
      statusUpdatedAt: at, statusSource: `auto-resume (${how})`, updatedBy: by, updatedAt: at });
    await addRecordChange({ record: `projects/${projectId}`, field: 'status', label: 'Status',
      from: 'On Hold', to, reason: `Taken off hold automatically — ${how}`,
      by, byName: byName || by, at, money: false });
    invalidateCache();
    return true;
  } catch (e) { console.warn('[resumeIfHeld]', projectId, e.message); return false; }
}

// Never overwrites: if someone else took this number since the page loaded,
// the save fails and says so instead of replacing their instruction.
export function addInstruction(projectId, seq, data) {
  const ref = doc(db, orgPath(`projects/${projectId}/instructions/${seq}`));
  return runTransaction(db, async (tx) => {
    if ((await tx.get(ref)).exists()) throw new Error(`Instruction ${seq} was just added by someone else. Close this and add it again.`);
    // orgId on every instruction (multi-tenant step 2): the collection-group
    // index query and rule filter on it.
    tx.set(ref, { ...data, orgId: ORG_ID });
  }).then(async () => {
    // A transaction writes to the server only — never to the local cache — and
    // listInstructions reads the cache first, so the page redrew with the new
    // instruction missing (8619 #2, Karan 21/10/2026). Reading it back puts it
    // in the cache before the caller re-renders.
    await getDocFromServer(ref).catch(() => {});
  });
}
// Edit an existing instruction from the browser (#2+ only — instruction 1 is written by the server callables only; the rules enforce it, and a fee never changes here).
export function updateInstruction(projectId, seq, fields) {
  return updateDoc(doc(db, orgPath(`projects/${projectId}/instructions/${seq}`)), fields);
}
// ── Add instruction redesign (Karan 26/10/2026) ──────────────────────────────
// Add and Edit go through the server (createInstruction / updateInstructionCalc,
// functions/instruction-create.js): the number is taken in a transaction, the
// fee maths is redone there, a won enquiry is opened or linked behind it.
const notDeployed = (e) => e && (e.code === 'functions/unimplemented' || (e.code === 'functions/not-found' && /^not[-_ ]?found$/i.test(e.message || '')));
async function instructionCall(name, data) {
  let out;
  try { out = await callFunction(name, data); }
  catch (e) {
    if (notDeployed(e)) throw new Error('Adding instructions this way isn’t available yet — the server side of this release still needs deploying.');
    throw new Error((e && e.message) || String(e));
  }
  // The write happened on the server: read the instruction back so the page's
  // local-first read shows it (the 8619 #2 lesson, as addInstruction).
  if (out && out.seq) await getDocFromServer(doc(db, orgPath(`projects/${data.projectId}/instructions/${out.seq}`))).catch(() => {});
  invalidateCache('projects');
  return out;
}
export const createInstructionServer = (data) => instructionCall('createInstruction', data);
export const updateInstructionCalc = (data) => instructionCall('updateInstructionCalc', data);
// v4.20.0: Set the fee / Revise the fee on any instruction, instruction 1 included
// (functions/instruction-fee.js) — the history is kept there, never written here.
export const setInstructionFee = (data) => instructionCall('setInstructionFee', data);
// The fee backfill (Owner/Director): a dry run by default; dryRun:false applies
// only the ticked rows of a dry run's log. And the latest run, read back.
export async function backfillInstructionFees(data) {
  try { return await callFunction('backfillInstructionFees', data); }
  catch (e) {
    if (notDeployed(e)) throw new Error('The fee backfill isn’t available yet — the server side of this release still needs deploying.');
    throw new Error((e && e.message) || String(e));
  }
}
export async function instructionFeeBackfillRun(data = {}) {
  try { return await callFunction('instructionFeeBackfillRun', data); }
  catch (e) {
    if (notDeployed(e)) return { run: null, rows: [], notDeployed: true };
    throw new Error((e && e.message) || String(e));
  }
}

// The draft as you go — projects/{pid}/instructionDrafts/{uid}, readable and
// writable by its owner only (firestore.rules). One draft per person per project.
const draftRef = (projectId) => {
  const uid = auth && auth.currentUser && auth.currentUser.uid;
  return uid ? doc(db, orgPath(`projects/${projectId}/instructionDrafts/${uid}`)) : null;
};
export async function getInstructionDraft(projectId) {
  const ref = draftRef(projectId);
  if (!ref) return null;
  const snap = await getDoc(ref).catch(() => null);
  return snap && snap.exists() ? snap.data() : null;
}
export function saveInstructionDraft(projectId, data) {
  const ref = draftRef(projectId);
  return ref ? setDoc(ref, { ...data, updatedAt: new Date().toISOString() }) : Promise.resolve();
}
export function deleteInstructionDraft(projectId) {
  const ref = draftRef(projectId);
  return ref ? deleteDoc(ref) : Promise.resolve();
}
// Draft with AI for a project's new instruction (no enquiry behind it yet):
// the same generateFeeProposal pricing, from the brief. Returns { ok, draft }.
export async function feeDraftForInstruction(projectId, brief) {
  try { return await callFunction('generateFeeProposal', { projectId, brief }); }
  catch (e) {
    if (notDeployed(e)) throw new Error('Draft with AI for an instruction isn’t available yet — the server side of this release still needs deploying.');
    throw new Error((e && e.message) || String(e));
  }
}

export async function listSubcollection(projectId, name, max = 100) {
  const snap = await getDocs(query(collection(db, orgPath(`projects/${projectId}/${name}`)), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Proper-case for client/contact names (Karan 21/08 — "Sean allen" read wrong
// everywhere and mis-sorts naive lists): lift a lowercase FIRST letter per
// word, never touch the rest — "ICS Ltd", "McDonald", "HSSP" all survive.
// Applied at the write chokepoints below so every entry path stays clean.
const SMALL_WORDS = new Set(['of','and','or','the','a','an','at','by','for','in','on','to','de','la','van','von','der','c/o']);
export function properName(s) {
  return String(s || '').replace(/\s+/g, ' ').trim()
    .replace(/(^|[\s\-'’(])([a-z][^\s\-'’(]*)/g, (m, pre, w, off) =>
      pre + (((off > 0 && SMALL_WORDS.has(w)) || (/['’]/.test(pre) && w.length === 1)) ? w : w.charAt(0).toUpperCase() + w.slice(1)));
}
const NAME_FIELDS = ['clientName', 'clientContact', 'clientCompany', 'name', 'contactName'];
function withProperNames(fields) {
  const out = { ...fields };
  for (const k of NAME_FIELDS) {
    if (typeof out[k] === 'string' && out[k] && !out[k].includes('@')) out[k] = properName(out[k]);
  }
  return out;
}
export function updateEnquiry(id, fields) {
  return updateDoc(doc(db, orgPath(`enquiries/${id}`)), withProperNames(fields));
}
export function addEnquiryDraft(data) { return addDoc(col('enquiries'), withProperNames(data)); }
export async function projectByNumber(number) {
  const snap = await getDocs(query(col('projects'), where('number', '==', String(number)), fbLimit(1)));
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}
export async function enquiryByProjectNo(number) {
  const snap = await getDocs(query(col('enquiries'), where('projectNo', '==', String(number)), fbLimit(1)));
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}
// Void cascade (Karan 17/08): a voided project was really an enquiry that never
// became work, so the knowledge moves BACK to the pipeline as a Lost enquiry —
// client, scope and any fee proposal stay on it, and the existing Reopen path is
// the revival. Flips the linked enquiry to Lost, or creates one when the project
// (typically V1-synced) never had an enquiry record. Never throws into the Void
// write — callers surface failures as a toast; the Void itself has landed.
export async function cascadeVoidToEnquiry(p, reason, session) {
  const at = new Date().toISOString();
  const why = `Project ${p.number || p.id} voided — ${(reason || '').trim() || 'never started'}`;
  const enq = p.number ? await enquiryByProjectNo(p.number) : null;
  if (enq) {
    if (enq.status === 'Lost') return { mode: 'already-lost', enq };
    await updateEnquiry(enq.id, { status: 'Lost', lostAt: at, lostReason: why, updatedBy: session.email, updatedAt: at });
    await addRecordChange({ record: 'enquiries/' + enq.id, field: 'status', label: 'Status',
      from: enq.status, to: 'Lost', reason: why, by: session.email, byName: session.displayName, at });
    return { mode: 'flipped', enq };
  }
  // No enquiry ever existed (pre-V2 project) — capture one so the client/scope
  // survive as a reopenable dead enquiry. receivedAt is required: the enquiry
  // list orders by it and Firestore drops docs missing the orderBy field.
  const ref = await addDoc(col('enquiries'), {
    status: 'Lost', number: null, projectNo: String(p.number || ''),
    clientName: p.clientName || null, clientRef: p.clientRef || null,
    schemeGuess: p.title || p.description || null, keyword: p.keyword || null,
    site: (p.siteAddress && p.siteAddress.line1) || p.site || null,
    receivedAt: p.createdAt || at, lostAt: at, lostReason: why,
    source: 'void-cascade', createdAt: at, updatedBy: session.email, updatedAt: at,
  });
  await addRecordChange({ record: 'enquiries/' + ref.id, field: 'status', label: 'Status',
    from: null, to: 'Lost', reason: why, by: session.email, byName: session.displayName, at });
  invalidateCache('enquiries');
  return { mode: 'created', enqId: ref.id };
}
// The one true delete (edit-patterns 6g right): genuinely removes an enquiry that
// never became anything. Firestore rules must permit this delete for Owner only.
export function deleteEnquiryDoc(id) { return deleteDoc(doc(db, orgPath(`enquiries/${id}`))); }
export async function updateClient(id, fields) {
  await updateDoc(doc(db, orgPath(`clients/${id}`)), withProperNames(fields));
  // The Clients list is a session memo (allOf): without this a rename showed the
  // old name until the 2-minute re-read or a reload (Karan 22/10, Mellor
  // Bromley → Jay Sandhu).
  invalidateCache('clients');
}

export async function listCatalogue(name) { // 'catalogueItems' | 'catalogueServices'
  const snap = await getDocs(query(col(name), fbLimit(500)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export function saveCatalogue(name, id, data) {
  return id ? setDoc(doc(db, orgPath(`${name}/${id}`)), data, { merge: true })
            : addDoc(col(name), data);
}
export async function allSuppliers() {
  const snap = await getDocs(query(col('suppliers'), orderBy('name'), fbLimit(1000)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export function addSupplier(data) { return addDoc(col('suppliers'), data); }
export function updateSupplier(id, fields) {
  return updateDoc(doc(db, orgPath(`suppliers/${id}`)), fields);
}

// Purchase orders — PO-first workflow (Karan 07/07): a PO is REQUESTED on the
// Procurement screen, APPROVED by a DIFFERENT Finance/Director (rules enforce
// four-eyes), then the expense attaches to it and the bank payment is matched.
export function addPurchaseOrder(data) { return addDoc(col('purchaseOrders'), data); }
export async function listPurchaseOrders({ userEmail = null, max = 500 } = {}) {
  const clauses = userEmail ? [where('userEmail', '==', userEmail)] : [];
  const snap = await getDocs(query(col('purchaseOrders'), ...clauses, fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export function updatePurchaseOrder(id, fields) {
  return updateDoc(doc(db, orgPath(`purchaseOrders/${id}`)), fields);
}
// v4.18.0: supplier invoices, matched to the order (server-written; Owner/Director/Finance read).
export async function listSupplierInvoices({ max = 400 } = {}) {
  const snap = await getDocs(query(col('supplierInvoices'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
// v4.19.1: remittance advice made from Bank's confirmed matches (server-written; Owner/Director/Finance read).
export async function listRemittances({ max = 400 } = {}) {
  const snap = await getDocs(query(col('remittances'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
// What the supplier-invoice intake read from the accounts address (a DRY RUN until it is switched on).
export async function listSupplierInvoiceIntakeLog({ max = 100 } = {}) {
  const snap = await getDocs(query(col('supplierInvoiceIntakeLog'), orderBy('at', 'desc'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// --- Bank reconciliation (Karan 07/07: stop line lifted for MANUAL allocation).
// Every write here is a human-confirmed action in Track's own data — the
// Revolut connection itself stays read-only, always. ---

// Attach/detach an allocation on a bank transaction (finance + step-up, rules-gated).
export function setTxnAllocation(txnId, allocation, session) {
  return updateDoc(doc(db, orgPath(`bankTransactions/${txnId}`)), {
    allocation, updatedBy: session.email, updatedAt: new Date().toISOString(),
  });
}

// Categorise a bank transaction (Karan 10/07). V2-owned; the sync never writes it.
export function setTxnCategory(txnId, category, session) {
  return updateDoc(doc(db, orgPath(`bankTransactions/${txnId}`)), {
    category, categoryBy: session.email, updatedBy: session.email, updatedAt: new Date().toISOString(),
  });
}

// ── Finance FY 2026–27 (pilot) — the Bank ledger, Overview and Accounts ─────
// Bank data stays behind the step-up in the rules; these are the reads the new
// screens make (and the harness answers by name). A month at or before
// settings.money.lockedThrough refuses category / VAT / allocation changes.

/** The bank accounts (Revolut), for the balance and the feed's last sync. */
export async function listBankAccounts() {
  const snap = await getDocs(query(col('bankAccounts'), fbLimit(50)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/**
 * Bank lines from `fromDay` ('YYYY-MM-DD', on completedAt), newest first — the
 * whole window, not a sample (the FY 2025–26 backfill puts 1,000+ lines in the
 * collection) — plus the lines still pending (no completedAt yet). Capped at `max`.
 * Single-field indexes only (completedAt range + order; state equality).
 */
export async function listBankTransactions({ fromDay = '', max = 4000 } = {}) {
  const [done, pending] = await Promise.all([
    getDocs(query(col('bankTransactions'), where('completedAt', '>=', fromDay || '2000-01-01'), orderBy('completedAt', 'desc'), fbLimit(max))),
    getDocs(query(col('bankTransactions'), where('state', '==', 'pending'), fbLimit(100))).catch(() => ({ docs: [] })),
  ]);
  const seen = new Map();
  for (const d of [...pending.docs, ...done.docs]) seen.set(d.id, { id: d.id, ...d.data() });
  return [...seen.values()];
}
/** The rule memory (categoryRules/{counterpartyKey}) as a Map. */
export async function listCategoryRules() {
  const snap = await getDocs(query(col('categoryRules'), fbLimit(2000)));
  return new Map(snap.docs.map((d) => [d.id, d.data()]));
}
/** Teach a rule: written on every confirmed category (money-model.js nextRule shapes it). */
export function saveCategoryRule(key, rule) {
  return setDoc(doc(db, orgPath(`categoryRules/${key}`)), rule);
}
/**
 * A person sets a line's category and/or VAT rate (Bank ledger). `fields`
 * may carry `category` and/or `vatRate` (20 | 5 | 0 | null).
 */
export function setTxnLedger(txnId, fields, session) {
  const at = new Date().toISOString();
  const patch = { updatedBy: session.email, updatedAt: at };
  if ('category' in fields) Object.assign(patch, { category: fields.category, categoryBy: session.email });
  if ('vatRate' in fields) Object.assign(patch, { vatRate: fields.vatRate, vatRateBy: session.email, vatRateAt: at });
  if ('split' in fields) patch.split = fields.split || null;   // Split into two… (money-model splitPlan; null removes it)
  return updateDoc(doc(db, orgPath(`bankTransactions/${txnId}`)), patch);
}
/** Credit notes (issued in Track): Sales on the P&L are invoices less these. */
export async function listCreditNotes() {
  const snap = await getDocs(query(col('creditNotes'), fbLimit(1000)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/** The ledger's state without the step-up: the To-categorise count, the lock, the feed (functions/money-ledger.js). */
export const moneyStatus = () => callFunction('moneyStatus', {});
export const lockMoneyMonth = (month) => callFunction('lockMoneyMonth', { month });
export const unlockMoneyMonth = (month, reason) => callFunction('unlockMoneyMonth', { month, reason });
export const saveMoneySettings = (patch) => callFunction('saveMoneySettings', { patch });
// Finance FY 2026–27 stages 5–6 (PILOT): Accounts › Cash flow (bank data — the step-up) and VAT.
export const cashFlowFigures = (window = 'six') => callFunction('cashFlowFigures', { window });
export const vatFigures = () => callFunction('vatFigures', {});
/**
 * Loans (Accounts › Loan, loans/{id}). The Owner reads every loan; a Director or
 * Finance reads only those with visibleTo 'finance' (the rules refuse the rest, so
 * the query asks for exactly those).
 */
export async function listLoans({ owner = false } = {}) {
  const q = owner ? query(col('loans'), fbLimit(50)) : query(col('loans'), where('visibleTo', '==', 'finance'), fbLimit(50));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/** The Owner adds (id null) or edits a loan: { name, lender, openingBalance, openedOn, closedOn, note, visibleTo }. */
export async function saveLoan(id, fields, session) {
  const ref = id ? doc(db, orgPath(`loans/${id}`)) : doc(col('loans'));
  await setDoc(ref, { ...fields, by: session.email, at: new Date().toISOString() });
  return ref.id;
}
/** Every bank line allocated to a loan (allocation.kind 'loan'). Bank data: the step-up. */
export async function listLoanRepayments() {
  const snap = await getDocs(query(col('bankTransactions'), where('allocation.kind', '==', 'loan'), fbLimit(2000)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Track-side paid marker on an invoice (v2Payment is V2-owned: the V1 sync
// never writes it, so the master sheet stays untouched until the write-back).
export function setInvoicePayment(invoiceId, v2Payment, session) {
  return updateDoc(doc(db, orgPath(`invoices/${invoiceId}`)), {
    v2Payment, updatedBy: session.email, updatedAt: new Date().toISOString(),
  });
}

// V2-owned instruction re-assignment on an invoice (golden thread, Karan 23/07).
// Like v2Payment this is V2-only: the V1 sync sets instructionRef but never reads
// v2InstructionSeq back, so the master sheet stays untouched until the write-back.
export function setInvoiceInstruction(invoiceId, seq, session) {
  return updateDoc(doc(db, orgPath(`invoices/${invoiceId}`)), {
    v2InstructionSeq: Number(seq), updatedBy: session.email, updatedAt: new Date().toISOString(),
  });
}

// V2-owned void marker on an invoice (edit-patterns 6g, Finance/Owner/Director).
// Like v2Payment/v2InstructionSeq this is V2-ONLY: the V1 sync never reads these
// fields back, so the master spreadsheet stays the invoice write master. Voiding
// writes Track data only (never the V1 sheet, never the bank). NOTE: the exact
// void data model is provisional — v2Voided/v2VoidReason mirror the v2Payment
// shape and need Karan's sign-off if a different model is preferred.
export function setInvoiceVoid(invoiceId, voidReason, session) {
  return updateDoc(doc(db, orgPath(`invoices/${invoiceId}`)), {
    v2Voided: true,
    v2VoidReason: voidReason || null,
    v2VoidedBy: session.email,
    v2VoidedAt: new Date().toISOString(),
    updatedBy: session.email, updatedAt: new Date().toISOString(),
  });
}

// Notes — part of the project record (handoff-ask-track 21b): written from the
// record or from the Ask Track bar, one list, visible to everyone on the project.
export function addNote(data) { return addDoc(col('notes'), data); }
export async function listNotes(max = 150) {
  const snap = await getDocs(query(col('notes'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function notesForProject(projectNo, max = 100) {
  return cachedQuery(query(col('notes'), where('projectNo', '==', String(projectNo)), fbLimit(max)),
    { key: `notes:${projectNo}` });
}

// Find indexes for the Ask Track bar (Karan 08/08: "build everything") — every
// instruction across every project via a collection-group query (rules carry a
// matching collection-group read), and the document-control register as the
// files index. Both capped and fetched lazily on first search.
export async function allInstructionsIndex(max = 1000) {
  // Only this org's (multi-tenant step 2b): the rule allows nothing else, and
  // the cap is no longer shared with other orgs' instructions.
  const snap = await getDocs(query(collectionGroup(db, 'instructions'), where('orgId', '==', ORG_ID), fbLimit(max)));
  return snap.docs
    .filter((d) => d.ref.path.startsWith(`organisations/${ORG_ID}/projects/`))
    .map((d) => ({ id: d.id, projectId: d.ref.parent.parent.id, ...d.data() }));
}
export async function allDocumentsIndex(max = 800) {
  const snap = await getDocs(query(col('documents'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Feedback / complaints register.
export function addFeedback(data) { return addDoc(col('feedback'), data); }
export async function listFeedback(max = 400) {
  const snap = await getDocs(query(col('feedback'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
// Feedback/complaints tied to one project (the golden thread on the project page).
export async function feedbackForProject(projectNo, max = 50) {
  const snap = await getDocs(query(col('feedback'),
    where('projectNo', '==', String(projectNo)), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export function updateFeedback(id, fields) {
  return updateDoc(doc(db, orgPath(`feedback/${id}`)), fields);
}

// Per-project issues log (Karan 16/07): issues found on a project + the action.
export function addProjectIssue(data) { return addDoc(col('projectIssues'), data); }
export async function projectIssuesForProject(projectNo, max = 50) {
  const snap = await getDocs(query(col('projectIssues'), where('projectNo', '==', String(projectNo)), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export function updateProjectIssue(id, fields) {
  return updateDoc(doc(db, orgPath(`projectIssues/${id}`)), fields);
}

// Document-control register (written by the track@ capture layer).
/**
 * Drawing markup v2, phase 3: the register row for a markup sent to the
 * architect. The rule allows only an outgoing 'markup' row the sender made.
 */
export async function addMarkupRegisterEntry(data) {
  const ref = await addDoc(col('documents'), data);
  return ref.id;
}
export async function documentsForProject(projectNo, max = 400) {
  return cachedQuery(query(col('documents'),
    where('projectNo', '==', String(projectNo)), fbLimit(max)), { key: `documents:${projectNo}` });
}
// AI usage/token log (Finance+ only) for the Costs screen. Bounded read.
export async function listAiUsage(max = 5000) {
  const snap = await getDocs(query(col('aiUsage'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
// search_standards audit log (spec 03) — Owner-only by rule; the Costs screen
// only calls these for an Owner, so nobody else sees a permission error.
export async function listStandardsSearches(max = 50) {
  const snap = await getDocs(query(col('standardsSearchLog'), orderBy('at', 'desc'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function getStandardsSearch(id) {
  const snap = await getDoc(doc(db, orgPath(`standardsSearchLog/${id}`)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Shared TRACK// email index (archiver-written) — visible to every org member,
// unlike the per-user Gmail panel. Newest first.
/**
 * The shared TRACK// index for one project.
 *
 * `alsoNo` is the A-number the project was enquired under. A thread tagged
 * TRACK// A0209 keeps that tag in its subject line for the life of the job, so
 * mail about the project can be indexed under the enquiry number — everything
 * indexed before the archiver learned to resolve a won enquiry (09/09), and
 * anything the repair has not repointed yet. Reading both is what stops those
 * emails disappearing at the moment the enquiry becomes a project.
 */
export function projectEmails(projectNo, max = 25, alsoNo = null) {
  // 5-min TTL — Home re-issued 8 of these on every render (perf audit 08/08).
  return ttl(`ts:mail:${projectNo}:${alsoNo || '-'}:${max}`, 300000, async () => {
    const keys = [String(projectNo), ...(alsoNo ? [String(alsoNo)] : [])];
    // The whole job's index is read, THEN cut to `max`. limit(max) with no order
    // returned the first `max` by document id, so a busy job (8415 had 43
    // threads) showed an arbitrary 25 and could drop its newest mail. There is
    // no orderBy('date') because `date` comes in two types (see mail-index.js);
    // a job's index is tens of rows, so reading it all is cheap.
    const snaps = await Promise.all(keys.map((k) => getDocs(query(col('projectEmails'),
      where('projectNo', '==', k), fbLimit(500))).catch(() => ({ docs: [] }))));
    const byId = new Map();
    for (const snap of snaps) for (const d of snap.docs) if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() });
    return newestFirst([...byId.values()]).slice(0, max);
  });
}
/** Every mail-index row for a set of project / enquiry numbers (the client page's contacts). */
export function emailsForNumbers(numbers = []) {
  const keys = [...new Set(numbers.filter(Boolean).map(String))].sort();
  return ttl(`ts:mailnos:${keys.join(',')}`, 300000, async () => {
    const chunks = [];
    for (let i = 0; i < keys.length; i += 30) chunks.push(keys.slice(i, i + 30));
    const snaps = await Promise.all(chunks.map((ks) => getDocs(query(col('projectEmails'),
      where('projectNo', 'in', ks), fbLimit(1000))).catch(() => ({ docs: [] }))));
    return snaps.flatMap((s) => s.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}
export async function documentsNeedingReview(max = 200) {
  const snap = await getDocs(query(col('documents'),
    where('needsReview', '==', true), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export function updateDocument(id, fields) {
  return updateDoc(doc(db, orgPath(`documents/${id}`)), fields);
}
/**
 * Move to bin (v4.23.10): the register rows that point at these Drive files.
 * Writers disagree on the pointer (archiver: driveFileId, site media: driveId,
 * older rows: only driveUrl — see project-detail drDriveId), so both id fields
 * are queried, and with a project number its rows are matched by URL too.
 * Read from the SERVER (never the cache): the status read here is the one Undo
 * puts back.
 */
export async function registerRowsForFiles(ids = [], projectNo = null) {
  const want = [...new Set((ids || []).filter(Boolean).map(String))];
  if (!want.length) return [];
  const byId = new Map();
  const add = (snap) => snap.docs.forEach((d) => { if (!byId.has(d.id)) byId.set(d.id, { id: d.id, ...d.data() }); });
  for (let i = 0; i < want.length; i += 30) {
    const chunk = want.slice(i, i + 30);
    const [a, b] = await Promise.all([
      getDocs(query(col('documents'), where('driveFileId', 'in', chunk), fbLimit(500))),
      getDocs(query(col('documents'), where('driveId', 'in', chunk), fbLimit(500))),
    ]);
    add(a); add(b);
  }
  if (projectNo) {
    const set = new Set(want);
    const urlId = (u) => (String(u || '').match(/\/d\/([A-Za-z0-9_-]{20,})/) || String(u || '').match(/[?&]id=([A-Za-z0-9_-]{20,})/) || [])[1] || null;
    const snap = await getDocs(query(col('documents'), where('projectNo', '==', String(projectNo)), fbLimit(1000)));
    snap.docs.forEach((d) => { const r = d.data(); if (!byId.has(d.id) && !r.driveFileId && !r.driveId && set.has(urlId(r.driveUrl))) byId.set(d.id, { id: d.id, ...r }); });
  }
  return [...byId.values()];
}
/** Set a register row's status (binned on Move to bin, what it was on Undo). The rule lets a reviewer change status + updatedBy/At only. */
export function setRegisterStatus(id, status, by) {
  return updateDoc(doc(db, orgPath(`documents/${id}`)), { status: String(status), updatedBy: String(by || ''), updatedAt: new Date().toISOString() });
}

// Site visits are locally captured first, then upserted with deterministic IDs
// as connectivity returns. This makes retries idempotent.
export function saveSiteVisit(projectId, visitId, fields) {
  const clean = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined && v !== null));
  return setDoc(doc(db, orgPath(`projects/${projectId}/visits/${visitId}`)), clean, { merge: true });
}
export async function listSiteVisits(projectId, max = 30) {
  return cachedQuery(query(collection(db, orgPath(`projects/${projectId}/visits`)), orderBy('startedAt', 'desc'), fbLimit(max)),
    { key: `visits:${projectId}` });
}
export function addSiteDocument(id, fields) {
  return setDoc(doc(db, orgPath(`documents/${id}`)), fields, { merge: false });
}
export function updateProjectActivity(projectId, email) {
  const now = new Date().toISOString();
  return updateDoc(doc(db, orgPath(`projects/${projectId}`)), { lastActivityAt: now, updatedAt: now, updatedBy: email });
}

// Narrow V2-native field updates on sync-owned docs (rules enforce the field list).
export function updateUserFields(id, fields) {
  return updateDoc(doc(db, orgPath(`users/${id}`)), fields);
}
// Add a team member from the Team page (Owner/Director/Finance). V2-native — the
// V1 sync upserts, never deletes, so this coexists. Keyed on the email-local slug
// (the same convention the sync uses: abdel, ahmed, …). Karan 03/08.
/**
 * Mint this user's Firebase Auth claims from their Track record, now.
 *
 * Adding someone used to write this Firestore record and nothing else — and a
 * record grants nothing. The claim is what admits them, and it was minted only
 * by the Apps Script sync, which reads the V1 spreadsheet's People tab and so
 * never saw anybody created here (Karan 18/09). Owners and Directors only;
 * returns { state: 'granted' | 'revoked' | 'awaiting-first-sign-in' }.
 */
// Track Intelligence on mail (Mail v2, 06/10/2026). Server-written; the rules let each
// person read only their own, so the query MUST carry uid == me. One equality
// filter, no orderBy — no composite index; sorted by the caller.
export async function listAiThreads(max = 600) {
  const uid = auth && auth.currentUser && auth.currentUser.uid;
  if (!uid) return [];
  const snap = await getDocs(query(col('aiThreads'), where('uid', '==', uid), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function listAiActions(max = 200) {
  const uid = auth && auth.currentUser && auth.currentUser.uid;
  if (!uid) return [];
  const snap = await getDocs(query(col('aiActions'), where('uid', '==', uid), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Mail rules (07/10/2026). Org-wide; everyone reads, Owners/Directors edit, any
// member may add a sender rule in their own name (firestore.rules). The run
// order and the evaluation are public/mail-rules.js.
export async function listMailRules() {
  const snap = await getDocs(query(col('mailRules'), fbLimit(500)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function addMailRule(rule) {
  const ref = await addDoc(col('mailRules'), rule);
  return { id: ref.id, ...rule };
}
export function updateMailRule(id, patch) {
  return setDoc(doc(db, orgPath(`mailRules/${id}`)), patch, { merge: true });
}
export function deleteMailRule(id) { return deleteDoc(doc(db, orgPath(`mailRules/${id}`))); }

// Track's own snooze (Gmail's API cannot snooze): the thread is archived and
// this records when it returns; mailSnoozeWake puts it back. Each person's own.
const snoozeId = (uid, threadId) => `${uid}_${String(threadId).replace(/[^A-Za-z0-9_-]/g, '')}`;
export async function listMailSnoozes() {
  const uid = auth && auth.currentUser && auth.currentUser.uid;
  if (!uid) return [];
  const snap = await getDocs(query(col('mailSnoozes'), where('uid', '==', uid), fbLimit(300)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export function setMailSnooze(threadId, fields) {
  const uid = auth && auth.currentUser && auth.currentUser.uid;
  if (!uid) return Promise.reject(new Error('Not signed in'));
  return setDoc(doc(db, orgPath(`mailSnoozes/${snoozeId(uid, threadId)}`)), { ...fields, uid, threadId });
}
export function deleteMailSnooze(threadId) {
  const uid = auth && auth.currentUser && auth.currentUser.uid;
  if (!uid) return Promise.resolve();
  return deleteDoc(doc(db, orgPath(`mailSnoozes/${snoozeId(uid, threadId)}`)));
}

/** Any callable, for modules that must not import Firebase themselves (google-api.js). */
export async function callFunction(name, data) {
  const fns = getFunctions(app, 'europe-west2');
  return (await httpsCallable(fns, name)(data || {})).data;
}

export async function grantAccess(userId) {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'grantAccess')({ userId });
  invalidateCache('users');
  return res.data;
}

/**
 * Finish your own set-up on first sign-in. Called from the provisioning screen:
 * if an Owner or Director has already added you, this writes your claims now
 * rather than leaving you waiting on a sync that never looks at this database.
 */
export async function claimAccess() {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'claimAccess')({});
  return res.data;
}

/**
 * The company a just-granted person is joining, for the "You're in" chip
 * (sign-in and access redesign, 23/10/2026). Refreshes the token so the new
 * org claim applies, then reads the org document the rules already let a
 * member read. Null when anything is missing — the chip is simply left out.
 */
export async function joinedOrgName() {
  const user = auth && auth.currentUser;
  if (!user) return null;
  const res = await user.getIdTokenResult(true);
  const orgId = res && res.claims && typeof res.claims.org === 'string' ? res.claims.org : null;
  if (!orgId) return null;
  const snap = await getDoc(doc(db, `organisations/${orgId}`));
  if (!snap.exists()) return null;
  const d = snap.data() || {};
  return (d.branding && typeof d.branding.displayName === 'string' && d.branding.displayName) || (typeof d.name === 'string' && d.name) || null;
}

export async function addTeamMember({ displayName, email, roles, chargeOutRate, leaveEntitlement }) {
  const local = String(email || '').split('@')[0].toLowerCase().replace(/[^a-z0-9]+/g, '') || 'member';
  const id = local + '-' + Math.random().toString(36).slice(2, 5); // avoid clobbering a synced id
  await setDoc(doc(db, orgPath(`users/${id}`)), {
    displayName: displayName || null, email: email || null,
    roles: Array.isArray(roles) ? roles : (roles ? [roles] : []),
    chargeOutRate: chargeOutRate || null, leaveEntitlement: leaveEntitlement || null,
    active: true, createdBy: null, createdAt: new Date().toISOString(),
  });
  invalidateCache('users');
  return id;
}
export async function updateProjectFields(id, fields) {
  await updateDoc(doc(db, orgPath(`projects/${id}`)), fields);
  invalidateCache('projects'); // drop the stale allProjects() cache so the edit shows (audit 23/07)
}
// Change a project's client (v4.6.2, Karan 26/10/2026): server-side
// (changeProjectClient), because the link and name follow but invoices already
// raised stay — and clientRef is not a field the rules let a browser edit.
// pick: { id } for a client on file, or { newName } for a new one.
export async function changeProjectClient(projectId, pick = {}) {
  const data = pick.id ? { projectId, clientId: pick.id } : { projectId, newClientName: pick.newName };
  let out;
  try { out = await callFunction('changeProjectClient', data); }
  catch (e) {
    if (e && (e.code === 'functions/unimplemented' || (e.code === 'functions/not-found' && /^not[-_ ]?found$/i.test(e.message || '')))) {
      throw new Error('Changing the client isn’t available yet — the server side of this release still needs deploying.');
    }
    throw new Error((e && e.message) || String(e));
  }
  // The write happened on the server, so the local cache still holds the old
  // client: read the project back before the page redraws (as addInstruction).
  await getDocFromServer(doc(db, orgPath(`projects/${projectId}`))).catch(() => {});
  invalidateCache('projects');
  invalidateCache('clients');
  return out;
}
// The stored `lead` is a path string to a user doc (same shape the sync writes),
// so the UI can reassign a lead inline. ORG_ID keeps it multi-tenant-clean.
export const leadRefPath = (userId) => (userId ? orgPath(`users/${userId}`) : null);

// Re-read an enquiry's original email with the intake model. Dry run by default:
// returns { current, proposed, wouldWrite, verdict, … } and writes nothing.
// apply:true fills EMPTY fields only (never overwrites a person's edit).
export async function reextractEnquiry(enquiryId, apply = false) {
  const fns = getFunctions(app, 'europe-west2');
  try {
    const res = await httpsCallable(fns, 'reextractEnquiry')({ enquiryId, apply: !!apply });
    return res.data;
  } catch (e) {
    // The front-end can ship ahead of a functions deploy — say so plainly
    // rather than surfacing "internal" at someone who can't act on it.
    if (e && (e.code === 'functions/not-found' || e.code === 'functions/unimplemented')) {
      throw new Error('AI re-read isn’t available yet — the server side of this release still needs deploying.');
    }
    throw e;
  }
}

// Fee-proposal agent (server-side LLM draft). Returns { ok, draft }. The draft
// is also written to the enquiry's `feeDraft` field by the function.
export async function generateFeeDraft(enquiryId) {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'generateFeeProposal')({ enquiryId });
  return res.data;
}

// "Think further" — re-price the enquiry through the strongest available model.
// Returns { ok, draft, escalationId, original }. Does NOT overwrite feeDraft.
export async function escalateFeeDraft(enquiryId) {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'escalateFeeProposal')({ enquiryId });
  return res.data;
}
// Log whether the user kept the escalated result (best-effort — never throws).
export async function setEscalationKept(id, kept) {
  try {
    const fns = getFunctions(app, 'europe-west2');
    await httpsCallable(fns, 'setEscalationKept')({ id, kept: !!kept });
  } catch (_) { /* analytics only */ }
}

// Capture an approved fee proposal as a learned example (best-effort — never blocks approve).
export async function captureFeeExample(enquiryId) {
  try {
    const fns = getFunctions(app, 'europe-west2');
    await httpsCallable(fns, 'captureFeeExample')({ enquiryId });
  } catch (_) { /* learning is best-effort */ }
}

// ── Drawing review: comments ────────────────────────────────────────────────
// A comment on a sheet is a TEAM record, not a private annotation: the engineer
// who marks up a detail and the coordinator who answers it are looking at the
// same Drive file from different machines. So comments live in Firestore keyed
// on the Drive file id (+ page), not in the session — which is where they lived
// until 12/09/2026, meaning every one was thrown away the moment the sheet was
// closed.
export async function listDrawingComments(fileId, page = null) {
  if (!fileId) return [];
  const clauses = [where('fileId', '==', String(fileId))];
  if (page != null) clauses.push(where('page', '==', Number(page)));
  const snap = await getDocs(query(col('drawingComments'), ...clauses, fbLimit(500)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (Number(a.n) || 0) - (Number(b.n) || 0));
}
export async function addDrawingComment(data) {
  const ref = await addDoc(col('drawingComments'), data);
  return ref.id;
}
/**
 * A new mark, with its id known NOW. `await addDoc` does not resolve until the
 * server answers, so with no connection the caller sat with no id and every later
 * edit or delete waited behind it. The document written is the same as addDoc's;
 * `written` settles when the server has it, or refuses it.
 */
export function createDrawingComment(data) {
  const ref = doc(col('drawingComments'));
  return { id: ref.id, written: setDoc(ref, data) };
}
/**
 * What the server has not confirmed yet on one sheet, from Firestore rather than
 * memory, so it is right after a reload with writes still queued.
 */
export function watchDrawingSync(fileId, onState, onError = null) {
  if (!fileId) return () => {};
  return onSnapshot(
    query(col('drawingComments'), where('fileId', '==', String(fileId)), fbLimit(1000)),
    { includeMetadataChanges: true },
    (snap) => onState({
      pending: snap.docs.filter((d) => d.metadata.hasPendingWrites).length,
      total: snap.size,
      fromCache: snap.metadata.fromCache,
    }),
    (e) => onError?.(e),
  );
}
export function updateDrawingComment(id, fields) {
  return updateDoc(doc(db, orgPath(`drawingComments/${id}`)), fields);
}
export function deleteDrawingComment(id) {
  return deleteDoc(doc(db, orgPath(`drawingComments/${id}`)));
}

/**
 * THE AUDIT TRAIL (Karan 24/09: "so if ever needed in future it can be
 * retrieved").
 *
 * Append-only, and separate from the comment itself on purpose. A comment
 * document shows what a mark says NOW; this shows what was asked, of whom, and
 * what happened next — which is the thing anybody ever actually needs, and the
 * thing a mutable document destroys every time somebody edits it.
 *
 * Nothing here is ever updated or deleted. A wrong entry is followed by a
 * corrected one, the way a site diary works.
 */
export async function logDrawingAudit(entry) {
  const ref = await addDoc(col('drawingAudit'), {
    ...entry,
    at: new Date(),
  });
  return ref.id;
}

/** Everything that ever happened to one sheet's markup, oldest first. */
export async function listDrawingAudit(fileId, max = 500) {
  const snap = await getDocs(query(col('drawingAudit'),
    where('fileId', '==', String(fileId)), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.at?.toDate?.() || 0) - (b.at?.toDate?.() || 0));
}

/**
 * A markup that somebody has to answer becomes a TASK, in the same collection
 * the Tasks module reads — projectId and state, not projectNo and status. That
 * distinction cost a week of invisible tasks in September.
 *
 * AND IT NEVER RAISED ONE (found 05/10). The record carried `assignee`, `due`,
 * no priority, no createdBy and source 'drawing-review', so the rules refused
 * every one of them; the caller swallowed the refusal and printed "task not
 * raised" in grey. It is now built by newTask() — the one shape the rules
 * accept — as a SYSTEM task raised by the person pressing the button, with the
 * job resolved from its number and the assignee from their email.
 */
export async function taskFromDrawing({ projectId, projectNo, title, assignee, fileId, commentId, page, by }) {
  const email = String(assignee || '').trim().toLowerCase();
  const [projects, users] = await Promise.all([
    projectId || !projectNo ? [] : allProjects().catch(() => []),
    email ? allUsers().catch(() => []) : [],
  ]);
  const pid = projectId || (projects.find((p) => String(p.number) === String(projectNo)) || {}).id || null;
  const who = users.find((u) => String(u.email || '').toLowerCase() === email) || null;
  if (email && !who) throw new Error(`${email} is not on the team in Track, so the task has nobody to go to.`);
  const body = newTask({
    title: String(title || 'Drawing markup').slice(0, 300),
    projectId: pid,
    assigneeId: who ? who.id : null,
    assigneeEmail: who ? email : null,
    source: 'system', sourceLabel: 'DRAWING REVIEW',
    sourceRef: `drive:${fileId || ''}#${commentId || ''}`,
    createdBy: by || null,
  });
  const at = new Date().toISOString();
  const ref = await addDoc(col('tasks'), {
    ...body,
    // The way back to the mark. Without these a task says "check dimension"
    // and nobody can find which drawing, which is worse than no task.
    drawingFileId: fileId || null,
    drawingCommentId: commentId || null,
    drawingPage: page || 1,
    createdAt: at, lastTouchedAt: at,
  });
  return ref.id;
}

/**
 * The latest design-audit report, or null. Owner only — the rules refuse
 * everybody else, so a denial here is the boundary working, not a fault.
 */
export async function latestAudit() {
  try {
    const snap = await getDoc(doc(db, orgPath('audits/latest')));
    return snap.exists() ? snap.data() : null;
  } catch (_) { return null; }
}

export async function askLibrarian(question) {
  const fns = getFunctions(app, 'europe-west2');
  // 120 s: the server's own deadline is 90 s (spec 05 E); the SDK default is 70.
  const res = await httpsCallable(fns, 'askLibrarian', { timeout: 120000 })({ question });
  return res.data;
}

/**
 * Ask Track a question about ONE job, answered from Track's own records.
 *
 * The thread that is about to be drawn is what gets sent, so the answer and the
 * line under it are the same arithmetic — see functions/ask-records.js. Returns
 * { status:'answered', answer, openAt } or { status:'nothing'|'limited', gap }.
 */
// ── Email signatures ────────────────────────────────────────────────────────
// Track holds what the signature says; the Cloud Function writes it into Gmail.
// The org block is Owner-writable (rules: affectedKeys hasOnly 'signature'), so
// the dotted paths below matter — a whole-map write would clobber autoPush and
// the skip list every time somebody edited the terms URL.
/** WhatsApp release switch (top-level org field, Owner-writable; wa-access.js reads it). */
export async function orgWhatsappRelease() {
  const snap = await getDoc(doc(db, `organisations/${ORG_ID}`));
  const d = snap.exists() ? snap.data() : {};
  return { everyone: !!(d.whatsappRelease && d.whatsappRelease.everyone),
    by: (d.whatsappRelease && d.whatsappRelease.by) || null, at: (d.whatsappRelease && d.whatsappRelease.at) || null };
}
// Settings stage 4: through saveSettings (the per-row tier, Owner; who and when stamped by
// the server; a settingsLog entry). The rules no longer let a browser write it. `by` is ignored.
export function setOrgWhatsappRelease(everyone, by) { // eslint-disable-line no-unused-vars
  return callFunction('saveSettings', { patch: { 'whatsappRelease.everyone': !!everyone }, screen: 'mail' });
}
export async function orgSignatureConfig() {
  const snap = await getDoc(doc(db, `organisations/${ORG_ID}`));
  return snap.exists() ? (snap.data().signature || null) : null;
}
/**
 * Save the org's signature block — and VERSION it (§10).
 *
 * Team shows each person as Current · v3, Outdated · v2 or Not set, and that
 * needs a number for the template itself. It is not a hash: a hash tells you
 * two things differ, and what a person reading the Team page wants is how far
 * behind somebody is. So the template carries a plain counter, bumped whenever
 * its CONTENT changes — not when autoPush is toggled or somebody is added to
 * the skip list, neither of which changes a word of what goes out.
 */
// Settings stage 4: the save goes through saveSettings (functions/settings-stage4.js): the
// Owner's row, the fields checked by the same validateOrgSignature, the version bumped on the
// server when a word changes, each change logged. The rules no longer let a browser write it.
// The derived officeTelE164 is the server's to set, so it is not sent.
// autoPush is a switch (Settings › Switches, stage 2): it goes through flipSwitch, which logs
// it and wants a reason to take it live — the reason here is where it was turned on.
export async function saveOrgSignature(fields) {
  const patch = {};
  for (const [k, v] of Object.entries(fields || {})) if (!['version', 'officeTelE164', 'autoPush'].includes(k)) patch[`signature.${k}`] = v;
  let out = { ok: true, saved: {} };
  if (Object.keys(patch).length) out = await callFunction('saveSettings', { patch, screen: 'signature' });
  if (fields && typeof fields.autoPush === 'boolean') {
    await callFunction('flipSwitch', { key: 'signature.autoPush', to: fields.autoPush, reason: 'Turned on in Team › Signature settings, after a preview.', screen: 'signature' });
  }
  return out;
}
/**
 * Render (and optionally push) signatures. Owner/Director only, server-enforced.
 * dryRun returns each person's rendered HTML and writes nothing to Gmail.
 */
export async function pushSignatures({ dryRun = true, uids = null, self = false, force = false } = {}) {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'pushSignatures')({
    orgId: ORG_ID, dryRun, ...(self ? { self: true } : uids ? { uids } : {}), ...(force ? { force: true } : {}),
  });
  return res.data;
}
/**
 * "Send test to me", the email half (v3, 31/10/2026): emails the SIGNED-IN
 * caller their own signature. Takes no address — the server reads the recipient
 * from the caller's verified sign-in and from nowhere else.
 */
export async function emailSignatureTest() {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'emailSignatureTest')({});
  return res.data;
}
/** The push receipts, newest first (Owner/Director read; server writes). */
export async function listSignaturePushes(max = 50) {
  const snap = await getDocs(query(col('signaturePushes'), orderBy('at', 'desc'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function askRecords({ question, subject, thread, emails = [] }) {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'askRecords')({ question, subject, thread, emails });
  return res.data;
}

// Google Places address lookup — proxied server-side (key in Secret Manager).
export async function placesAutocomplete(input, sessionToken) {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'placesProxy')({ mode: 'autocomplete', input, sessionToken });
  return (res.data && res.data.suggestions) || [];
}
export async function placeDetails(placeId, sessionToken) {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'placesProxy')({ mode: 'details', placeId, sessionToken });
  return res.data;
}
// Driving miles office → site → back, for mileage prefill/audit. `dest` is either
// { lat, lng } (a saved pin) or { address } (geocoded by Routes). Returns { oneWayMiles, roundTripMiles }.
export async function drivingMiles(dest) {
  const fns = getFunctions(app, 'europe-west2');
  const payload = { mode: 'route' };
  if (dest && dest.lat != null && dest.lng != null) { payload.destLat = dest.lat; payload.destLng = dest.lng; }
  else if (dest && dest.address) { payload.destAddress = dest.address; }
  const res = await httpsCallable(fns, 'placesProxy')(payload);
  return res.data;
}

// Cloud (Firebase/Google Cloud) spend summary, written by the billing
// summariser from the BigQuery billing export. Null until the first run.
export async function cloudCostSummary() {
  const snap = await getDoc(doc(db, orgPath('cloudCosts/summary')));
  return snap.exists() ? snap.data() : null;
}
// Claude credits this cycle (v4.7.0) — Owner/Director, read-only, server-computed.
export async function llmCredits() {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'llmCredits')({});
  return res.data;
}
export async function refreshCloudCosts() {
  const fns = getFunctions(app, 'europe-west2');
  const res = await httpsCallable(fns, 'refreshCloudCosts')({});
  return res.data;
}
export async function listLibraryFaqSeeds(max = 100) {
  const snap = await getDocs(query(col('libraryFaqSeeds'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((x) => x.active !== false);
}
// Every document the Ask Track search has indexed, one summary doc per source
// (or per part of a big source) — built by scripts/library-manifest.py.
export async function listKnowledgeFiles() {
  const snap = await getDocs(col('knowledgeFiles'));
  const bySource = new Map();
  for (const d of snap.docs.map((x) => x.data()).sort((a, b) => (a.part || 1) - (b.part || 1))) {
    const s = bySource.get(d.sourceId);
    if (s) s.files.push(...(d.files || []));
    else bySource.set(d.sourceId, { ...d, files: [...(d.files || [])] });
  }
  return [...bySource.values()];
}
export async function listKnowledgeSources(max = 100) {
  const snap = await getDocs(query(col('knowledgeSources'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((x) => x.approved === true);
}

// Pending status suggestions the automation engine raised for a project (the
// one-click confirms). Single-field query, then filter state client-side (no
// composite index needed).
export async function listPendingSuggestions(projectNo) {
  if (!projectNo) return [];
  const rows = await cachedQuery(query(col('statusSuggestions'), where('projectNo', '==', String(projectNo))),
    { key: `suggestions:${projectNo}` });
  return rows.filter((s) => s.state === 'pending');
}

// --- KPI counts: server-side aggregation, ~1 read each (never fetches rows) ---

async function count(q) {
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

export function countProjectsWhereStatusIn(statuses) {
  return count(query(col('projects'), where('status', 'in', statuses)));
}

export function countEnquiriesWhereStatus(status) {
  return count(query(col('enquiries'), where('status', '==', status)));
}

/**
 * WHAT TRACK HOLDS — nine aggregate counts for the Track-in-numbers screen (§15).
 *
 * Every one is a server-side count(), so this is nine reads and no documents:
 * counting 2,970 projects by fetching them would be 2,970 reads and about a
 * megabyte, for nine numbers on a screen that is opened by triple-clicking a
 * logo. Hours are a sum() over the same index for the same reason.
 *
 * Each returns null rather than throwing when the rules deny the collection, so
 * the screen shows a dash for what this person cannot see and the rest of the
 * numbers still arrive.
 */
export async function trackInNumbers() {
  const safe = (p) => p.then((n) => n).catch(() => null);
  const countAll = (name) => safe(count(query(col(name))));
  const [projects, enquiries, clients, instructions, documents, notes, tasksDone, answers, minutes] = await Promise.all([
    countAll('projects'), countAll('enquiries'), countAll('clients'),
    safe(count(query(collectionGroup(db, 'instructions'), where('orgId', '==', ORG_ID)))),
    countAll('documents'), countAll('notes'),
    safe(count(query(col('tasks'), where('state', '==', 'done')))),
    countAll('recordQuestions'),
    // Hours are the one figure that cannot be an aggregate: minutes live inside
    // each week's `rows[].days{}` map, so there is no field to sum() over. It
    // came from metrics/dashboard, which the retired V1 sync built from the V1
    // sheet (17/10) — so the screen now says it is not available rather than
    // show a July figure or fetch 600 timesheets to add them up.
    Promise.resolve(null),
  ]);
  return { projects, enquiries, clients, instructions, documents, notes, tasksDone, answers, minutes };
}

/** The org's settings map — rates, ceilings and the Track-in-numbers inputs. */
export async function orgSettings() {
  const snap = await getDoc(doc(db, `organisations/${ORG_ID}`));
  return (snap.exists() && snap.data().settings) || {};
}

export function countAll(name) {
  return count(query(col(name)));
}

// --- Finance: the old phone Summary (section 'finance', people off the FY 2026–27 pilot) ---
// v4.36.4: it used to sum() `amount` over three server aggregates — gross-or-net as V1
// stored it, voided and Unknown (V1) invoices included, and a Date-vs-ISO-string
// dueDate range that never matched — and returned fields the phone never read, so
// every figure showed "—". Now: the SAME invoice ledger desktop Finance reads
// (allInvoices, cache-first), net, Unknown (V1) + voided out (overview-figures.js
// financeStatsFrom), in exactly the shape mobile/screens/section.js buildFinance reads.
export async function financeStats({ money = {}, now = new Date(), startMonth = 11 } = {}) {
  const [invoices, projects, { financeStatsFrom }] = await Promise.all([allInvoices(), allProjects().catch(() => null), import('./overview-figures.js')]);
  return financeStatsFrom({ invoices, projects, money, now, startMonth });
}

// Most recent invoices by date (bounded).
export async function recentInvoices(pageSize = 25) {
  const snap = await getDocs(query(col('invoices'), orderBy('invoiceDate', 'desc'), fbLimit(pageSize)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ── Tasks (design_handoff_tasks_module, 28/08/2026) ─────────────────────────
// The record and the selectors live in tasks.js, which is pure and tested; this
// is only the Firestore end. Days ('YYYY-MM-DD') are strings by design — see
// the note at the top of tasks.js.
//
// Today is TWO queries, not one. "Committed to today" is an equality on
// plannedDate and "overdue" is a range on dueDate, and Firestore will not take
// both in a single query without an or() whose cost and index shape are worse
// than two bounded reads. partitionToday() dedupes, so a task that is both
// overdue and planned appears once.

const taskDoc = (id) => doc(db, orgPath(`tasks/${id}`));
const touch = (session) => ({
  lastTouchedAt: new Date().toISOString(),
  updatedBy: session?.email || null,
});

/** Everything the Today panel needs for one person on one day. */
/**
 * TURN 7/8 READS. Everyone sees everyone's tasks, loads and hours — the module
 * is a team view and there is no role gating on it (HANDOFF §1). So the Tasks
 * page takes ONE read of the open work and every view groups it in the browser:
 * four queries for four views would drift, and the load figure has to agree
 * with itself in five places.
 */
export async function teamTasks(max = 500) {
  const snap = await getDocs(query(col('tasks'), where('state', '!=', 'done'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Today's finished work, for the board's "Done today" column and the digest.
 *  Kept separate so the open-work read stays small and predictable. */
export async function doneToday(day, max = 200) {
  const snap = await getDocs(query(col('tasks'), where('state', '==', 'done'),
    where('completedAtDay', '==', day), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Live team view: the page re-renders when anyone moves anything, which is
 *  what makes the 09:15 standup readable off one screen. */
export function watchTeamTasks(cb, max = 500) {
  return onSnapshot(query(col('tasks'), where('state', '!=', 'done'), fbLimit(max)),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => { console.warn('[tasks] live view lost:', err.message); cb(null); });
}

/** Every task on one job, for the project record's Tasks tab. */
export async function projectTasks(projectId, max = 200) {
  const snap = await getDocs(query(col('tasks'), where('projectId', '==', projectId), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Ask Track's drafts, awaiting Accept. It may draft and suggest; it never
 *  creates silently (HANDOFF §12). */
export async function taskSuggestions(max = 20) {
  const snap = await getDocs(query(col('tasks'), where('source', '==', 'agent'),
    where('state', '==', 'todo'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((t) => t.accepted !== true);
}

/** Days-since-contact per client, written nightly by clientContactDaily.
 *  { byId: { clientId: {lastContactAt, via} }, computedAt } — 10-minute TTL. */
export function clientInsights() {
  return ttl('clientInsights', 600000, async () => {
    const snap = await getDocs(query(col('clientInsights'), fbLimit(3000)));
    const byId = {};
    let computedAt = null;
    snap.docs.forEach((d) => { if (d.id === '_run') computedAt = d.data().computedAt || null; else byId[d.id] = d.data(); });
    return { byId, computedAt };
  });
}

/** Tasks raised from a client's "Suggested this week" (sourceRef clients/{id}#{key}). */
export async function clientActionTasks(clientId) {
  const lo = `clients/${clientId}#`;
  const snap = await getDocs(query(col('tasks'), where('sourceRef', '>=', lo), where('sourceRef', '<', lo + '\uf8ff'), fbLimit(50)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createTask(fields, session) {
  const at = new Date().toISOString();
  const body = newTask({ ...fields, createdBy: session?.email || null });
  const ref = await addDoc(col('tasks'), { ...body, createdAt: at, lastTouchedAt: at });
  return { id: ref.id, ...body };
}

/** A contractor's task for the team (data/tasks.js createSiteTask, v4.0.1):
 *  the record is already in its checked shape; this stamps the times and
 *  writes it. ASP's tasks keep createTask above. */
export async function createTaskRecord(record) {
  const at = new Date().toISOString();
  const ref = await addDoc(col('tasks'), { ...record, createdAt: at, lastTouchedAt: at });
  return { id: ref.id, ...record, createdAt: at };
}

/** Patch a task. Always bumps lastTouchedAt, because decay is measured from it
 *  and an edit that did not count as a touch would make live work look stale. */
export function updateTask(id, patch, session) {
  return updateDoc(taskDoc(id), { ...patch, ...touch(session) });
}

/**
 * Move a task between states. Completing writes completedAt AND completedAtDay:
 * the board's Done column and the digest both ask "finished today?", and asking
 * that of a timestamp means a range query on every render.
 */
export function setTaskState(id, state, session) {
  const now = new Date();
  const done = state === 'done';
  return updateDoc(taskDoc(id), {
    state,
    completedAt: done ? now.toISOString() : null,
    completedAtDay: done ? dayKey(now) : null,
    ...touch(session),
  });
}

/** Commit a task to a day, or take it back off. This is the ONLY thing that
 *  writes plannedDate — quick-add must not, however clearly someone typed
 *  "fri" (HANDOFF §1). */
export function planTask(id, day, session) {
  return updateDoc(taskDoc(id), { plannedDate: day || null, ...touch(session) });
}

/** Reassign. Always through the capacity picker, never a bare dropdown (§13) —
 *  this function is what that picker calls once a person has seen the effect. */
export function assignTask(id, person, session) {
  return updateDoc(taskDoc(id), {
    assigneeId: person ? person.userId : null,
    assigneeEmail: person ? (person.email || null) : null,
    ...touch(session),
  });
}

/** Raise a blocker: the state and the blocker move together, because a blocked
 *  task with no blocker is the thing the whole column exists to prevent. */
export function blockTask(id, blocker, session, from) {
  // stateBeforeBlock is what Unblock returns it to. Nothing wrote it, so every
  // unblocked task fell back to To do, whatever it had been.
  const back = STATES.includes(from) && from !== 'blocked' && from !== 'done' ? from : 'todo';
  return updateDoc(taskDoc(id), { state: 'blocked', blocker, stateBeforeBlock: back, ...touch(session) });
}

/** Unblock: clears the blocker and returns the task to where it was (§13). */
export function unblockTask(id, backTo, session) {
  return updateDoc(taskDoc(id), {
    state: STATES.includes(backTo) && backTo !== 'blocked' ? backTo : 'todo',
    blocker: null, ...touch(session),
  });
}

/** The activity trail under a task. The blocker note appears here verbatim, in
 *  the person's words (§7) — so it is stored as typed and never summarised. */
export function taskActivity(id, max = 60) {
  return getDocs(query(collection(db, orgPath(`tasks/${id}/activity`)),
    orderBy('at', 'desc'), fbLimit(max)))
    .then((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })));
}

export function addTaskActivity(id, entry, session) {
  return addDoc(collection(db, orgPath(`tasks/${id}/activity`)), {
    ...entry,
    by: session?.email || null,
    byName: session?.displayName || null,
    at: new Date().toISOString(),
  });
}

export async function logTaskTime(session, entry, when = new Date()) {
  const { weekIdFor, isoDay, mondayOf, dayKeyOf, addMinutes } = await import('./task-time.js');
  const email = session?.email || '';
  // Step 6: a phone-only identity names its weeks by uid (me.js sheetKey).
  const id = email ? weekIdFor(email, when) : `${session?.uid || ''}_${isoDay(mondayOf(when))}`;
  const ref = doc(db, orgPath(`timesheets/${id}`));
  const day = dayKeyOf(when);
  invalidateTimesheets();
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur = snap.exists() ? snap.data() : null;
    // An APPROVED week is closed. Refusing is right — silently reopening someone
    // else's approved timesheet to add minutes would be worse than not booking.
    if (cur && cur.status === 'approved') throw new Error('That week is approved — the time was not booked.');
    const rows = addMinutes(cur ? cur.rows : [], { ...entry, day });
    tx.set(ref, {
      ...(cur || { weekStart: isoDay(mondayOf(when)), status: 'draft', userEmail: email,
                   ...(email ? {} : { ownerUid: session?.uid || '' }),
                   userName: session?.displayName || email }),
      rows,
      rev: Number((cur && cur.rev) || 0) + 1,
    }, { merge: true });
    return { id, day, rows: rows.length };
  });
}

/** One task by id. bankTaskTime needs the project fields to book the timesheet,
 *  and the page's in-memory list is not a reliable place to find them — a timer
 *  outlives the list it was started from. */
export async function getTask(id) {
  const snap = await getDoc(taskDoc(id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * One place where a stopped timer is dealt with, because there are five places
 * it can stop — ticking a task, pressing stop, and starting a second timer,
 * on two screens. Banking the seconds in one and forgetting in another is how a
 * person loses an afternoon, so no caller does this by hand.
 *
 * Both writes are attempted independently: if the timesheet refuses (an
 * approved week), the minutes are still recorded on the task, and the reason
 * comes back rather than disappearing.
 */
export async function bankTaskTime(session, { taskId, seconds, projectId, number, label, task }, when = new Date()) {
  const mins = Math.round((Number(seconds) || 0) / 60);
  const out = { minutes: mins, booked: false, error: null };
  if (!taskId || !seconds) return out;
  // ATOMIC, NOT READ-ADD-WRITE. This used to take the caller's in-memory
  // loggedSeconds, add to it and write the total back — a lost update waiting to
  // happen. With Track open in two tabs (or the live listener replacing the
  // local copy between the read and the write) the second write overwrote the
  // first with a SMALLER total, and an afternoon's logged time went backwards on
  // its own. increment() is resolved by the server against whatever is actually
  // stored, so two concurrent banks both land.
  await updateTask(taskId, { loggedSeconds: increment(Number(seconds)) }, session)
    .catch((e) => { out.error = e?.message || 'could not update the task'; });
  // Under a minute is not yet a minute on a timesheet booked in 0:01 steps.
  if (mins < 1) return out;
  try {
    await logTaskTime(session, { projectId: projectId || null, number: number || '', label: label || '', task: task || '', minutes: mins }, when);
    out.booked = true;
  } catch (e) {
    out.error = e?.message || 'could not book the time';
  }
  return out;
}


/** Commit a task to one of a day's three slots, or clear it back to "also".
 *  Committing is the priority call the module rests on, so it is its own write
 *  rather than a field somebody might set in passing. */

/** Ask Track dismissals, per person. A dismissal silences a PATTERN for thirty
 *  days (ASK-TRACK.md §6), and it belongs to the person rather than the browser
 *  — dismissing something on the desk should not have it reappear on the phone. */
export async function askDismissals(email) {
  const id = String(email || '').toLowerCase().replace(/[^a-z0-9@._-]/g, '_');
  const snap = await getDoc(doc(db, orgPath(`askDismissals/${id}`)));
  return snap.exists() ? (snap.data().patterns || {}) : {};
}

export function dismissPattern(email, patternKey, until) {
  const id = String(email || '').toLowerCase().replace(/[^a-z0-9@._-]/g, '_');
  return setDoc(doc(db, orgPath(`askDismissals/${id}`)),
    { patterns: { [patternKey]: until }, updatedAt: new Date().toISOString() }, { merge: true });
}

/**
 * The meeting-room display's configuration. A document per room rather than a
 * block on the org record: the room account is granted exactly this one
 * document, and a second room needs no schema change (Karan 17/09).
 */
export function roomConfig(roomId = 'main') {
  return getDoc(doc(db, orgPath(`rooms/${roomId}`))).then((s) => (s.exists() ? { id: s.id, ...s.data() } : null));
}
// Settings stage 4: through saveRoomSettings (the per-row tier, the same checks the rules made,
// who and when stamped by the server, a settingsLog entry). The rules no longer let a browser write it.
export function saveRoomConfig(roomId, fields) {
  return callFunction('saveRoomSettings', { roomId, fields, screen: 'team' });
}

// MEETING MODE (v4.0.0, 23/10/2026; docs/meeting-mode/SCHEMA-CHANGE.md — needs
// Karan's sign-off before deploy). One record per team meeting: who, in what
// order, who is on now, and the ids of what it made. The room display may read
// a record while `roomFollows` is on, so it holds no task or note TEXT, ever.
export async function createMeeting(data) {
  const ref = await addDoc(col('meetings'), data);
  return { id: ref.id, ...data };
}
export function updateMeeting(id, fields) {
  return updateDoc(doc(db, orgPath(`meetings/${id}`)), fields);
}
/** A follower puts their own name on the meeting (entry) or takes it off (null). */
export function followMeeting(id, uid, entry) {
  return updateDoc(doc(db, orgPath(`meetings/${id}`)), { [`followers.${uid}`]: entry || null });
}
/**
 * Projects written since a moment (ISO), live — meeting mode (v4.6.3, Karan
 * 26/10/2026: a stage moved in the meeting, or in another tab while it runs,
 * has to show in the meeting). Every V2 project write stamps `updatedAt` as an
 * ISO string (applyStatus, applyLead, the edit panel, the phone), so this is a
 * small range read on a single-field index — only what changed, never the
 * whole register. A project whose `updatedAt` is not a string is simply not
 * seen here (the meeting keeps the copy it opened with).
 */
export function watchProjectsUpdatedSince(since, cb, max = 300) {
  return onSnapshot(query(col('projects'), where('updatedAt', '>=', String(since || '')), fbLimit(max)),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => cb(null));
}
export function watchMeeting(id, cb) {
  return onSnapshot(doc(db, orgPath(`meetings/${id}`)),
    (s) => cb(s.exists() ? { id: s.id, ...s.data() } : null),
    () => cb(null));
}
/** Meetings marked running (the caller decides which are still live). */
export async function runningMeetings(max = 5) {
  const snap = await getDocs(query(col('meetings'), where('state', '==', 'running'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/** The latest finished meetings, newest first — "since the last meeting". */
export async function recentMeetings(max = 6) {
  const snap = await getDocs(query(col('meetings'), orderBy('endedAt', 'desc'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((m) => m.state === 'wrapped');
}
// What moved since a moment (ISO): one range read each, single-field indexes only.
export async function changesSince(iso, max = 600) {
  const snap = await getDocs(query(col('changes'), where('at', '>=', iso), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function tasksDoneSince(iso, max = 400) {
  const snap = await getDocs(query(col('tasks'), where('completedAt', '>=', iso), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
export async function notesSince(iso, max = 300) {
  const snap = await getDocs(query(col('notes'), where('createdAt', '>=', iso), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// FILES IN USE (Karan 09/10/2026, Files turn 8: "In use — You, in AutoCAD,
// since 09:12"). Google Drive cannot say who has a file open on their own
// computer, so Track keeps it: opening a file in its desktop program from
// Track marks it in use, "Done with it" clears it, and a mark older than 12
// hours counts as gone. Advisory only — it never stops anybody opening a file.
// One doc per Drive file id; everyone in the org reads, each writes their own.
export const FILE_USE_STALE_MS = 12 * 3600 * 1000;
const fileUseId = (fileId) => String(fileId).replace(/[^A-Za-z0-9_-]/g, '');
export function watchFileUse(project, cb) {
  return onSnapshot(query(col('fileUse'), where('project', '==', String(project))),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => cb([]));
}
export function markFileInUse({ fileId, project, name, app }) {
  const u = auth && auth.currentUser;
  if (!u) return Promise.reject(new Error('Not signed in'));
  const id = fileUseId(fileId);
  return setDoc(doc(db, orgPath(`fileUse/${id}`)), {
    fileId: id, project: String(project || ''), name: String(name || '').slice(0, 300), app: String(app || '').slice(0, 40),
    uid: u.uid, email: u.email || '', by: String(u.displayName || u.email || '').slice(0, 120), since: serverTimestamp(),
  });
}
export function clearFileInUse(fileId) {
  return deleteDoc(doc(db, orgPath(`fileUse/${fileUseId(fileId)}`)));
}

// WHATSAPP IN TRACK (17/10/2026, docs/whatsapp-contract.md). Every wa*
// collection is written by Cloud Functions only; org members read. The
// browser's writes go through the callables (waSend, waImportChat,
// waAssignThread, waRecordAs, waTemplatesSync) — never straight to Firestore —
// plus the two signed-off field additions it may write itself:
// clients/{id}.phones[] (updateClient) and users/{id}.waPhone (updateUserFields).
const waMs = (v) => { if (!v) return 0; if (typeof v.toMillis === 'function') return v.toMillis(); if (typeof v.seconds === 'number') return v.seconds * 1000; const t = new Date(v).getTime(); return Number.isNaN(t) ? 0 : t; };

/**
 * Live WhatsApp threads, newest first. { projectId } narrows to one project's
 * (an equality filter, sorted here, so no composite index is needed);
 * without it, the newest 300 in the org (Mail). cb(null) on a listener error.
 */
export function listenWaThreads(cb, { projectId = null, max = 300 } = {}) {
  const q = projectId
    ? query(col('waThreads'), where('projectId', '==', String(projectId)), fbLimit(max))
    : query(col('waThreads'), orderBy('lastMessageAt', 'desc'), fbLimit(max));
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => waMs(b.lastMessageAt) - waMs(a.lastMessageAt));
    cb(rows);
  }, () => cb(null));
}

/**
 * Live WhatsApp messages, oldest first: one thread's ({ threadId }) or every
 * message filed to a project ({ projectId }, the Correspondence timeline).
 */
export function listenWaMessages({ threadId = null, projectId = null, max = 500 } = {}, cb) {
  if (!threadId && !projectId) { cb([]); return () => {}; }
  const q = threadId
    ? query(col('waMessages'), where('threadId', '==', String(threadId)), fbLimit(max))
    : query(col('waMessages'), where('projectId', '==', String(projectId)), fbLimit(max));
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    rows.sort((a, b) => waMs(a.at) - waMs(b.at));
    cb(rows);
  }, () => cb(null));
}

/** The org's message templates (approved, submitted, draft, rejected). */
export async function waTemplates() {
  const snap = await getDocs(query(col('waTemplates'), fbLimit(100)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/** The connected WhatsApp numbers (the ASP number and the Track number). */
export async function waNumbers() {
  const snap = await getDocs(query(col('waNumbers'), fbLimit(20)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/** Past chat imports for a project, newest first. */
export async function waImportsFor(projectId) {
  const snap = await getDocs(query(col('waImports'), where('projectId', '==', String(projectId)), fbLimit(50)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => waMs(b.at) - waMs(a.at));
}
// The contract's callables (europe-west2). A person presses every one of these.
export const waSend = (args) => callFunction('waSend', args);
export const waImportChat = (args) => callFunction('waImportChat', args);
export const waAssignThread = (threadId, projectId) => callFunction('waAssignThread', { threadId, projectId });
export const waRecordAs = (msgId, kind) => callFunction('waRecordAs', { msgId, kind });
export const waTemplatesSync = () => callFunction('waTemplatesSync', {});
// The AI build's draft (never sends: { draft, audit, windowOpen, note?, sends: false }) and WhatsApp search for Ask Track.
export const waDraftReply = (threadId) => callFunction('waDraftReply', { threadId });
export const waSearch = (args) => callFunction('waSearch', args);

// ── CPD (v4.23.0, Karan 29/10/2026) ────────────────────────────────────────
// organisations/{org}/cpd/{id}: one document per CPD entry (cpd-model.js has
// the shape; docs/cpd/SCHEMA-CHANGE.md, signed off by Karan 29/10/2026). A person
// reads and writes their own (who.uid is their sign-in); the Owner and
// Directors read everyone's and write nobody else's (firestore.rules).
const CPD_FIELDS = ['date', 'topic', 'provider', 'kind', 'hours', 'learned', 'certificate'];
/** Your own CPD entries, newest first. */
export async function listMyCpd(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(col('cpd'), where('who.uid', '==', String(uid)), fbLimit(1000)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}
/** Everyone's CPD entries (the Owner and Directors; the rules refuse anyone else). */
export async function listTeamCpd(max = 3000) {
  const snap = await getDocs(query(col('cpd'), fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/**
 * Add an entry (cpd-model.js cleanEntry's output). A confirmed suggestion has a
 * fixed id (entryDocId), so it is written in a transaction that adds it only
 * if it is not there yet: confirming the same hours twice is one entry.
 * → { id, existed }
 */
export async function addCpdEntry(entry, id = null) {
  const data = { ...entry, createdAt: serverTimestamp(), updatedAt: serverTimestamp() };
  if (!id) { const ref = await addDoc(col('cpd'), data); return { id: ref.id, existed: false }; }
  const ref = doc(db, orgPath(`cpd/${id}`));
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) return { id, existed: true };
    tx.set(ref, data);
    return { id, existed: false };
  });
}
/** Change your own entry: only what a person types (never who, source or when it was made). */
export function updateCpdEntry(id, fields) {
  const out = {};
  for (const k of CPD_FIELDS) if (k in (fields || {})) out[k] = fields[k];
  return updateDoc(doc(db, orgPath(`cpd/${id}`)), { ...out, updatedAt: serverTimestamp() });
}
export function deleteCpdEntry(id) { return deleteDoc(doc(db, orgPath(`cpd/${id}`))); }
/** Your own timesheet weeks, for the CPD suggestions (the CPD row's hours). */
export async function myTimesheets({ userEmail = null, ownerUid = null, max = 120 } = {}) {
  const clause = userEmail ? where('userEmail', '==', userEmail) : ownerUid ? where('ownerUid', '==', ownerUid) : null;
  if (!clause) return [];
  const snap = await getDocs(query(col('timesheets'), clause, fbLimit(max)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
/** The company's CPD year and target (settings.cpd), set by the Owner or a Director.
 *  Settings stage 1 (01/11/2026): through saveSettings — the per-row check on the
 *  server and a Change log entry; the rules no longer let the browser write it. */
export function setCpdSettings({ targetHours, yearStartMonth }, by = '') {   // eslint-disable-line no-unused-vars
  return callFunction('saveSettings', { patch: { 'cpd.targetHours': Number(targetHours), 'cpd.yearStartMonth': Number(yearStartMonth) }, screen: 'cpd' });
}
